import jsPDF from "jspdf";
import { PDFDocument } from "pdf-lib";

let watermarkBase64Cache = null;
let _pdfGenerating = false; // prevents double-click / concurrent downloads

// Strip any trailing /api suffix so static-asset URLs are always correct,
// regardless of whether REACT_APP_API_URL was set with or without /api.
const _rawApiUrl = process.env.REACT_APP_API_URL || "http://localhost:5000";
const API_URL = _rawApiUrl.replace(/\/api\/?$/, "");

// ── HEADER HEIGHT CONSTANT ────────────────────────────────────────────────────
// Matches the watermark/letterhead header area on every page.
// All pages (first AND subsequent) must start content below this offset.
const HEADER_HEIGHT = 40;
const FOOTER_SAFE_SPACE = 50;

// ── DATE FORMATTING HELPER ────────────────────────────────────────────────────
// Ensures all dates in the MTC PDF are displayed in DD/MM/YYYY format.
function formatDateDDMMYYYY(dateValue) {
  if (!dateValue) return "";
  try {
    const str = String(dateValue).trim();
    const d = new Date(str);
    if (isNaN(d.getTime())) return str; // unparseable → return as-is
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  } catch {
    return String(dateValue);
  }
}

async function getWatermarkBase64() {
  // Return cached value immediately
  if (watermarkBase64Cache) return watermarkBase64Cache;

  try {
    const res = await fetch(`${API_URL}/templates/watermark.jpeg`);
    if (!res.ok) {
      console.warn(`[Watermark] HTTP ${res.status} – watermark will be skipped`);
      return null;
    }
    const blob = await res.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        watermarkBase64Cache = reader.result;
        resolve(reader.result);
      };
      reader.onerror = () => {
        console.warn("[Watermark] FileReader error – watermark will be skipped");
        resolve(null);
      };
      reader.readAsDataURL(blob);
    });
  } catch (err) {
    // Network error (server down, CORS, etc.) – don't crash PDF generation
    console.warn("[Watermark] Failed to fetch watermark:", err.message, "– PDF will be generated without letterhead");
    return null;
  }
}

function addWatermarkToPage(doc, watermarkBase64) {
  try {
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    if (watermarkBase64) {
      doc.addImage(watermarkBase64, "JPEG", 0, 0, pageWidth, pageHeight);
    }
  } catch (err) {
    console.warn("Failed to add watermark:", err.message);
  }
}

function getImageFormatFromDataUrl(dataUrl) {
  if (!dataUrl || typeof dataUrl !== "string") return "JPEG";
  if (dataUrl.startsWith("data:image/png")) return "PNG";
  if (dataUrl.startsWith("data:image/jpg") || dataUrl.startsWith("data:image/jpeg")) return "JPEG";
  const match = dataUrl.match(/^data:image\/(\w+);/i);
  return match ? match[1].toUpperCase() : "JPEG";
}

function addImageContained(doc, dataUrl, format, x, y, w, h, padding = 1) {
  if (!dataUrl) return;
  const props = doc.getImageProperties(dataUrl);
  if (!props || !props.width || !props.height) return;

  const maxW = Math.max(0, w - padding * 2);
  const maxH = Math.max(0, h - padding * 2);
  if (maxW === 0 || maxH === 0) return;

  const scale = Math.min(maxW / props.width, maxH / props.height, 1);
  const drawW = props.width * scale;
  const drawH = props.height * scale;
  const drawX = x + padding + (maxW - drawW) / 2;
  const drawY = y + padding + (maxH - drawH) / 2;

  const base64 = dataUrl.split(",")[1] || dataUrl;
  doc.addImage(base64, format, drawX, drawY, drawW, drawH);
}

// Helper: Convert Blob to DataURL
function blobToDataURL(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

// ── FETCH SIGNATURE BY REVIEWER NAME ──────────────────────────────────────────
async function fetchSignatureBase64(reviewerName) {
  if (!reviewerName || !reviewerName.trim()) {
    return null;
  }

  try {
    console.log(`[Signature Fetch] Starting for: "${reviewerName}"`);

    // Get auth token
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    const headers = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    // Step 1: Get signature path from API
    const url = `${API_URL}/api/mtc/signature/${encodeURIComponent(reviewerName.trim())}`;
    console.log(`[Signature Fetch] API URL: ${url}`);

    const response = await fetch(url, { headers });

    if (!response.ok) {
      console.warn(`[Signature Fetch] API returned ${response.status} for: "${reviewerName}"`);
      return null;
    }

     const data = await response.json();
     console.log(`[Signature Fetch] API Response:`, data);

     if (!data.success || !data.data || !data.data.signature_path) {
       console.warn(`[Signature Fetch] No signature path in response for: "${reviewerName}". Full response:`, data);
       return null;
     }

     // Step 2: Fetch the actual image file
     let signaturePath = data.data.signature_path;
     console.log(`[Signature Fetch] Signature path from DB: "${signaturePath}"`);

     // Build image URL robustly
     let imageUrl;
     try {
       imageUrl = new URL(signaturePath, API_URL).toString();
     } catch (e) {
       console.warn(`[Signature Fetch] URL construction failed, using fallback concatenation`);
       imageUrl = `${API_URL}${signaturePath.startsWith('/') ? '' : '/'}${signaturePath}`;
     }
     console.log(`[Signature Fetch] Image URL: ${imageUrl}`);

     const imgResponse = await fetch(imageUrl);
     if (!imgResponse.ok) {
       console.error(`[Signature Fetch] Failed to fetch image ${imageUrl}: ${imgResponse.status} ${imgResponse.statusText}`);
       // Try fallback: directly from uploads folder without API_URL prefix
       try {
         const fallbackUrl = signaturePath.startsWith('/') ? signaturePath : '/' + signaturePath;
         console.log(`[Signature Fetch] Trying fallback URL: ${fallbackUrl}`);
         const fallbackResp = await fetch(fallbackUrl);
         if (fallbackResp.ok) {
           const blob = await fallbackResp.blob();
           return blobToDataURL(blob);
         } else {
           console.error(`[Signature Fetch] Fallback also failed: ${fallbackResp.status}`);
           return null;
         }
       } catch (fallbackErr) {
         console.error(`[Signature Fetch] Fallback error:`, fallbackErr);
         return null;
       }
     }

     // Step 3: Convert to Base64 (keep full data URL for format detection)
     const blob = await imgResponse.blob();
     console.log(`[Signature Fetch] Image blob size: ${blob.size} bytes`);

     return new Promise((resolve) => {
       const reader = new FileReader();
       reader.onloadend = () => {
         const dataUrl = reader.result;
         console.log(`[Signature Fetch] ✓ Successfully converted to DataURL for "${reviewerName}": ${dataUrl.substring(0, 80)}...`);
         resolve(dataUrl); // Return full data URL (includes format info)
       };
       reader.onerror = () => {
         console.error(`[Signature Fetch] FileReader error for "${reviewerName}"`);
         resolve(null);
       };
       reader.readAsDataURL(blob);
     });
  } catch (err) {
    console.error(`[Signature Fetch] Error for "${reviewerName}":`, err);
    return null;
  }
}

/**
 * Generate MTC PDF based on valve type
 * Each valve type has its own PDF format matching reference images
 */
export async function generateMTCPdf(tcdsData, formData, valveType, heatAnnexureData = null, attachmentImages = null, attachmentPdfs = null) {
  if (_pdfGenerating) {
    console.warn("[PDF] Generation already in progress – ignoring duplicate call");
    return;
  }
  _pdfGenerating = true;

  try {
    return await _generateMTCPdfInternal(tcdsData, formData, valveType, heatAnnexureData, attachmentImages, attachmentPdfs);
  } finally {
    _pdfGenerating = false; // always release lock, even if an error is thrown
  }
}

async function _generateMTCPdfInternal(tcdsData, formData, valveType, heatAnnexureData = null, attachmentImages = null, attachmentPdfs = null) {
  const type = (valveType || "gate").toLowerCase();

  console.log("[PDF Generation] Starting for valve type:", type);
  console.log("[PDF Generation] Form data keys:", Object.keys(formData));
  console.log("[PDF Generation] prepared_by_name:", formData.prepared_by_name);
  console.log("[PDF Generation] approved_by:", formData.approved_by);
  console.log("[PDF Generation] prepared_by:", formData.prepared_by);

  let preparedBySignature = null;
  let approvedBySignature = null;
  let qacOfficerSignature = null;

  try {
    console.log("[PDF Generation] Fetching signatures...");
    const preparedByName = formData.prepared_by_name || formData.prepared_by;
    const approvedByName = formData.approved_by;
    const qacOfficerName = formData.qac_officer;

    console.log(`[PDF Generation] Will fetch signatures for: "${preparedByName}", "${approvedByName}", and "${qacOfficerName}"`);

    const [preparedSig, approvedSig, qacSig] = await Promise.all([
      fetchSignatureBase64(preparedByName),
      fetchSignatureBase64(approvedByName),
      qacOfficerName ? fetchSignatureBase64(qacOfficerName) : Promise.resolve(null)
    ]);
    preparedBySignature = preparedSig;
    approvedBySignature = approvedSig;
    qacOfficerSignature = qacSig;

    console.log("[PDF Generation] Signature fetch results:");
    console.log("[PDF Generation] - prepared_by_signature:", preparedBySignature ? "✓ Loaded" : "✗ Not found");
    console.log("[PDF Generation] - approved_by_signature:", approvedBySignature ? "✓ Loaded" : "✗ Not found");
    console.log("[PDF Generation] - qac_officer_signature:", qacOfficerSignature ? "✓ Loaded" : "✗ Not found");
  } catch (err) {
    console.error("[PDF Generation] Error fetching signatures:", err);
  }

  const enrichedFormData = {
    ...formData,
    prepared_by_signature: preparedBySignature,
    approved_by_signature: approvedBySignature,
    qac_officer_signature: qacOfficerSignature
  };

  console.log("[PDF Generation] Enriched form data with signatures");

  let result;
  switch (type) {
    case "ball":
      result = await generateBallValvePdf(tcdsData, enrichedFormData, heatAnnexureData, attachmentImages);
      break;
    case "lsb":
      result = await generateLSBPdf(tcdsData, enrichedFormData);
      break;
    case "actuator":
      result = await generateActuatorPdf(tcdsData, enrichedFormData);
      break;
    case "automation":
      result = await generateAutomationPdf(tcdsData, enrichedFormData, heatAnnexureData, attachmentImages);
      break;
    case "check":
      result = await generateCheckValvePdf(tcdsData, enrichedFormData, heatAnnexureData, attachmentImages);
      break;
    case "globe":
      result = await generateGlobeValvePdf(tcdsData, enrichedFormData, heatAnnexureData, attachmentImages);
      break;
    case "gate":
    default:
      result = await generateGateValvePdf(tcdsData, enrichedFormData, heatAnnexureData, attachmentImages);
      break;
  }

  const downloadPdf = (pdfBytes, fileName) => {
    const blob = new Blob([pdfBytes], { type: "application/pdf" });
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = objectUrl;
    link.download = fileName;
    link.style.display = "none";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
  };

  if (result && result.pdfBytes) {
    let downloaded = false;
    if (attachmentPdfs && attachmentPdfs.length > 0) {
      try {
        const mergedPdf = await mergePdfAttachments(result.pdfBytes, attachmentPdfs);
        downloadPdf(mergedPdf, result.fileName);
        downloaded = true;
      } catch (mergeErr) {
        console.error("[PDF] Error merging PDF attachments – downloading without attachments:", mergeErr);
      }
    }
    if (!downloaded) {
      downloadPdf(result.pdfBytes, result.fileName);
    }
  }
  
  return result?.fileName;
}

// Helper function to merge PDF attachments into main PDF
async function mergePdfAttachments(mtcPdfBytes, attachmentPdfs) {
  const mtcPdf = await PDFDocument.load(mtcPdfBytes);
  
  for (const pdfAttachment of attachmentPdfs) {
    try {
      let pdfBytes;
      
      if (pdfAttachment.url && pdfAttachment.url.startsWith('data:')) {
        // Handle base64 encoded PDF
        const base64Data = pdfAttachment.url.split(',')[1];
        const binaryString = atob(base64Data);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        pdfBytes = bytes.buffer;
      } else if (pdfAttachment.url) {
        // Fetch from URL
        const response = await fetch(pdfAttachment.url);
        pdfBytes = await response.arrayBuffer();
      } else if (pdfAttachment.data) {
        pdfBytes = pdfAttachment.data;
      }
      
      if (pdfBytes) {
        const externalPdf = await PDFDocument.load(pdfBytes);
        const pageIndices = externalPdf.getPageIndices();
        const copiedPages = await mtcPdf.copyPages(externalPdf, pageIndices);
        copiedPages.forEach(page => mtcPdf.addPage(page));
      }
    } catch (err) {
      console.warn("Failed to merge PDF attachment:", err.message);
    }
  }
  
  return await mtcPdf.save();
}

// ══════════════════════════════════════════════════════════════════════════════
// COMMON HELPERS
// ══════════════════════════════════════════════════════════════════════════════

function createPdfHelpers(doc) {
  const pw = doc.internal.pageSize.getWidth();   // 210
  const ML = 10;
  const TW = pw - ML - ML; // 190

  const drawCell = ({
    x, y: cy, w, h,
    text = "",
    bold = false,
    italic = false,
    fontSize = 7,
    align = "left",
    valign = "middle",
    fill = null,
    border = true,
    color = [0, 0, 0],
    pad = 1.5,
    wrap = false,
  }) => {
    if (fill) {
      doc.setFillColor(...fill);
      doc.rect(x, cy, w, h, "F");
    }
    if (border) {
      doc.setDrawColor(0, 0, 0);
      doc.setLineWidth(0.2);
      doc.rect(x, cy, w, h, "S");
    }
    if (text !== "" && text !== null && text !== undefined) {
      const str = String(text);
      doc.setFontSize(fontSize);
      doc.setFont("helvetica", italic ? "italic" : bold ? "bold" : "normal");
      doc.setTextColor(...color);

      const availableW = w - pad * 2;
      const lines = wrap ? doc.splitTextToSize(str, availableW) : [str];
      const lineH = fontSize * 0.4;
      const totalTextH = lines.length * lineH;
      const availableH = h - pad * 2;
      
      let startY;
      if (valign === "top") {
        startY = cy + pad + fontSize * 0.35;
      } else if (totalTextH <= availableH) {
        startY = cy + (h - totalTextH) / 2 + fontSize * 0.35;
      } else {
        startY = cy + pad + fontSize * 0.35;
      }

      lines.forEach((line, i) => {
        let tx = align === "center" ? x + w / 2
               : align === "right"  ? x + w - pad
               : x + pad;
        doc.text(line, tx, startY + i * lineH * 1.3, { align });
      });
    }
  };

  const val = (formData, tcdsData, ...keys) => {
    for (const k of keys) {
      const v = formData?.[k] ?? tcdsData?.[k];
      if (v !== undefined && v !== null && v !== "") return String(v);
    }
    return "";
  };

  return { doc, pw, ML, TW, drawCell, val };
}

// ══════════════════════════════════════════════════════════════════════════════
// HEAT ANNEXURE PAGE GENERATOR (Tables first, then images)
// ══════════════════════════════════════════════════════════════════════════════

function addHeatAnnexurePage(doc, heatAnnexureData, valveType, formData, attachmentImages = null, watermarkBase64 = null) {
  if (!heatAnnexureData || heatAnnexureData.length === 0) return;
  
  const { ML, TW, drawCell } = createPdfHelpers(doc);
  
  // Reference info - BATCH NO: <value> | QUANTITY: <value> NOS.
  const refLabel = valveType === 'ball' ? 'BATCH NO:' : 'SR. NUMBER:';
  const refValue = valveType === 'ball' 
    ? (formData.batch_no || formData.batchNo || "")
    : (formData.sr_no || formData.srNo || formData.section_number || "");
  
  // Calculate quantity from heat annexure data (unique valve numbers)
  const uniqueValveNos = [...new Set(heatAnnexureData.map(row => row.valveNo))];
  const quantity = formData.heat_annexure_qty || formData.qty || uniqueValveNos.length;
  
  // Table headers
  const colWidths = [25, 35, 35, 35, 30, 30];
  const headerTexts = ["VALVE NO.", "COMPONENT", "HEAT NO.", "MATERIAL", "TC NO.", "VENDOR NAME"];
  const hdrH = 8;
  const rowH = 7;
  
  // ============================================================
  // STEP 1: Render ALL Heat Annexure Tables (grouped together)
  // ============================================================
  doc.addPage();
  addWatermarkToPage(doc, watermarkBase64);
  // FIX: Start content below the header/watermark area on every new page
  let y = HEADER_HEIGHT;
  
  // Title
  drawCell({ x: ML, y, w: TW, h: 10, text: "HEAT ANNEXURE", bold: true, fontSize: 14, align: "center" });
  y += 12;
  
  // Draw SR NUMBER and QUANTITY in one row with boxes
  const halfW = TW / 2;
  drawCell({ x: ML, y, w: 30, h: 8, text: refLabel, bold: true, fontSize: 9, border: true });
  drawCell({ x: ML + 30, y, w: halfW - 30, h: 8, text: refValue, fontSize: 9, border: true });
  drawCell({ x: ML + halfW, y, w: 30, h: 8, text: "QUANTITY:", bold: true, fontSize: 9, border: true });
  drawCell({ x: ML + halfW + 30, y, w: halfW - 30, h: 8, text: `${quantity} NOS.`, fontSize: 9, border: true });
  y += 12;
  
  // Draw table headers
  let x = ML;
  headerTexts.forEach((text, i) => {
    drawCell({ x, y, w: colWidths[i], h: hdrH, text, bold: true, fontSize: 7, align: "center", fill: null });
    x += colWidths[i];
  });
  y += hdrH;
  
  // Draw all table rows
  let lastValveNo = null;
  
  heatAnnexureData.forEach((row) => {
    // Check if we need a new page for the table
    if (y > 270) {
      doc.addPage();
      addWatermarkToPage(doc, watermarkBase64);
      // FIX: Reset y to below header area on continuation pages
      y = HEADER_HEIGHT;
      
      // Repeat headers on new page
      let x = ML;
      headerTexts.forEach((text, i) => {
        drawCell({ x, y, w: colWidths[i], h: hdrH, text, bold: true, fontSize: 7, align: "center", fill: null });
        x += colWidths[i];
      });
      y += hdrH;
    }
    
    x = ML;
    const showValveNo = row.valveNo !== lastValveNo;
    drawCell({ 
      x, y, w: colWidths[0], h: rowH, 
      text: showValveNo ? String(row.valveNo) : "", 
      fontSize: 7, align: "center",
      fill: null,
      bold: showValveNo
    });
    lastValveNo = row.valveNo;
    x += colWidths[0];
    
    drawCell({ x, y, w: colWidths[1], h: rowH, text: row.component || "", fontSize: 6.5, align: "center" });
    x += colWidths[1];
    
    drawCell({ x, y, w: colWidths[2], h: rowH, text: row.heatNumber || "", fontSize: 6.5, align: "center" });
    x += colWidths[2];
    
    drawCell({ x, y, w: colWidths[3], h: rowH, text: row.material || "", fontSize: 6, align: "center" });
    x += colWidths[3];
    
    drawCell({ x, y, w: colWidths[4], h: rowH, text: row.tcNo || "", fontSize: 6.5, align: "center" });
    x += colWidths[4];
    
    drawCell({ x, y, w: colWidths[5], h: rowH, text: row.vendorName || "", fontSize: 6, align: "center" });
    
    y += rowH;
  });
  
  // ============================================================
  // STEP 2: Render Each Attachment Image on Separate Pages
  // ============================================================
  if (attachmentImages && attachmentImages.length > 0) {
    // Process each attachment in order
    for (let i = 0; i < attachmentImages.length; i++) {
      const imgData = attachmentImages[i];
      
      // Start a new page for each image
      doc.addPage();
      addWatermarkToPage(doc, watermarkBase64);
      // FIX: Start content below the header/watermark area on every new page
      y = HEADER_HEIGHT;
      
      // Title
      drawCell({ x: ML, y, w: TW, h: 10, text: "HEAT ANNEXURE", bold: true, fontSize: 14, align: "center" });
      y += 12;
      
      // Draw SR NUMBER and QUANTITY
      drawCell({ x: ML, y, w: 30, h: 8, text: refLabel, bold: true, fontSize: 9, border: true });
      drawCell({ x: ML + 30, y, w: halfW - 30, h: 8, text: refValue, fontSize: 9, border: true });
      drawCell({ x: ML + halfW, y, w: 30, h: 8, text: "QUANTITY:", bold: true, fontSize: 9, border: true });
      drawCell({ x: ML + halfW + 30, y, w: halfW - 30, h: 8, text: `${quantity} NOS.`, fontSize: 9, border: true });
      y += 15;
      
      if (imgData && imgData.url) {
        try {
          // Detect format from data URL
          let format = 'JPEG';
          if (imgData.url.startsWith('data:image/png')) {
            format = 'PNG';
          } else if (imgData.url.startsWith('data:image/jpeg') || imgData.url.startsWith('data:image/jpg')) {
            format = 'JPEG';
          } else if (imgData.url.startsWith('data:image/')) {
            const mimeMatch = imgData.url.match(/data:image\/(\w+)/);
            if (mimeMatch) {
              format = mimeMatch[1].toUpperCase();
              if (format === 'JPG') format = 'JPEG';
            }
          }
          
          // Reduced image dimensions
          const imgWidth = TW - 10;
          const imgHeight = 180;
          const imgX = ML + 5;
          
          // Add image
          doc.addImage(imgData.url, format, imgX, y, imgWidth, imgHeight);
          
          // Add border around image
          doc.setDrawColor(180, 180, 180);
          doc.setLineWidth(0.3);
          doc.rect(imgX, y, imgWidth, imgHeight);
          
          // Update y position after image
          y += imgHeight + 5;
        } catch (imgErr) {
          console.warn("Failed to add image:", imgErr.message);
          drawCell({ x: ML, y, w: TW, h: 30, text: "Image not available", fontSize: 7, align: "center", fill: null });
        }
      }
    }
  }
}

// ══════════════════════════════════════════════════════════════════════════════
// ATTACHMENT IMAGES PAGE GENERATOR
// ══════════════════════════════════════════════════════════════════════════════

function addAttachmentImagesPage(doc, attachmentImages, formData, valveType, watermarkBase64 = null) {
  if (!attachmentImages || attachmentImages.length === 0) return;
  
  const { ML, TW, drawCell } = createPdfHelpers(doc);
  
  // Add new page for Attachment Images
  doc.addPage();
  addWatermarkToPage(doc, watermarkBase64);
  // FIX: Start content below the header/watermark area on every new page
  let y = HEADER_HEIGHT;
  
  // Title
  const refLabel = valveType === 'ball' ? 'BATCH NO:' : 'SR. NUMBER:';
  const refValue = valveType === 'ball' 
    ? (formData.batch_no || formData.batchNo || "")
    : (formData.sr_no || formData.srNo || formData.section_number || "");
  
  drawCell({ x: ML, y, w: TW, h: 10, text: "ATTACHMENT IMAGES", bold: true, fontSize: 14, align: "center" });
  y += 12;
  
  drawCell({ x: ML, y, w: 30, h: 8, text: refLabel, bold: true, fontSize: 9, border: true });
  drawCell({ x: ML + 30, y, w: TW - 30, h: 8, text: refValue, fontSize: 9, border: true });
  y += 15;
  
  // Image configuration
  const maxImgWidth = TW / 2 - 5;  // 2 images per row, with spacing
  const maxImgHeight = 80;         // Maximum height for each image
  const imgGap = 10;
  const labelHeight = 8;
  
  // Process images in batches of 2 (for 2-column layout)
  for (let i = 0; i < attachmentImages.length; i++) {
    const imgData = attachmentImages[i];
    
    // Check if we need a new page for this image
    // Each image needs: maxImgHeight + labelHeight + gap = ~95mm per image row
    if (y + maxImgHeight + labelHeight + imgGap > 270) {
      doc.addPage();
      addWatermarkToPage(doc, watermarkBase64);
      // FIX: Reset y to below header area on continuation pages
      y = HEADER_HEIGHT;
    }
    
    // Calculate position - alternate between left and right columns
    const col = i % 2;
    const xPos = ML + (col * (maxImgWidth + imgGap));
    
    try {
      // Add image - detect format from URL (data:image/format;base64,...)
      if (imgData.url) {
        // Extract format from data URL or default to JPEG
        let format = 'JPEG';
        if (imgData.url.startsWith('data:image/png')) {
          format = 'PNG';
        } else if (imgData.url.startsWith('data:image/jpeg') || imgData.url.startsWith('data:image/jpg')) {
          format = 'JPEG';
        } else if (imgData.url.startsWith('data:image/')) {
          format = 'JPEG';
        }
        
        doc.addImage(imgData.url, format, xPos, y, maxImgWidth, maxImgHeight);
        
        // Add border around image
        doc.setDrawColor(180, 180, 180);
        doc.setLineWidth(0.3);
        doc.rect(xPos, y, maxImgWidth, maxImgHeight);
        
        y += maxImgHeight;
        
        // Add label below image
        const label = imgData.label || `Image ${i + 1}`;
        drawCell({ x: xPos, y, w: maxImgWidth, h: labelHeight, text: label, fontSize: 7, align: "center" });
        y += labelHeight + imgGap;
      }
    } catch (imgErr) {
      console.warn("Failed to add image:", imgErr.message);
      // Add placeholder for failed image
      drawCell({ x: xPos, y, w: maxImgWidth, h: maxImgHeight, text: "Image not available", fontSize: 7, align: "center", fill: null });
      y += maxImgHeight + labelHeight + imgGap;
    }
    
    // If this was the second image (right column), move to next row
    if (col === 1) {
      y += imgGap;
    }
  }
}

// ══════════════════════════════════════════════════════════════════════════════
// GATE VALVE PDF - API 600 Format (Image 1)
// ══════════════════════════════════════════════════════════════════════════════════════

async function generateGateValvePdf(tcdsData, formData, heatAnnexureData = null, attachmentImages = null) {
  try {
    const doc = new jsPDF("portrait", "mm", "a4");
    
    await getWatermarkBase64();
    addWatermarkToPage(doc, watermarkBase64Cache);
    
    const { ML, TW, drawCell, val: valHelper } = createPdfHelpers(doc);
    const val = (...keys) => valHelper(formData, tcdsData, ...keys);
    // First page: content starts below the header/watermark area
    let y = HEADER_HEIGHT;

    // ── TITLE ─────────────────────────────────────────────────────────────────
    drawCell({ x: ML, y, w: TW, h: 8, text: "INSPECTION & TEST CERTIFICATE", bold: true, fontSize: 11, align: "center", border: false });
    y += 8;
    drawCell({ x: ML, y, w: TW, h: 6, text: "ACCORDING TO EN 10204 TYPE 3.1", bold: true, fontSize: 9, align: "center", border: false });
    y += 6;

    // ── TOP INFO GRID ─────────────────────────────────────────────────────────
    const LW  = Math.round(TW * 0.55);
    const RW  = TW - LW;
    const lx  = ML;
    const rx  = ML + LW;
    const LL  = 38;
    const RL  = 30;
    const rH  = 7;

    const leftCell  = (lbl, txt) => {
      drawCell({ x: lx,      y, w: LL,      h: rH, text: lbl, bold: true, fontSize: 7 });
      drawCell({ x: lx + LL, y, w: LW - LL, h: rH, text: ": " + txt, fontSize: 7 });
    };
    const rightCell = (lbl, txt) => {
      drawCell({ x: rx,      y, w: RL,      h: rH, text: lbl, bold: true, fontSize: 7 });
      drawCell({ x: rx + RL, y, w: RW - RL, h: rH, text: ": " + txt, fontSize: 7 });
    };

    // Header rows
    leftCell("CERTIFICATE NO.",  val("certificate_no", "tcds_no") || "IPCIPL/U1/26-27/01");
    rightCell("DATE",            formatDateDDMMYYYY(val("date")) || formatDateDDMMYYYY(new Date()));
    y += rH;

    leftCell("CUSTOMER",         val("customer", "customer_name") || "");
    rightCell("TAG NO.",         val("tag_no") || "");
    y += rH;

    leftCell("P.O. NO. & DATE",  (val("po_no", "purchase_order_no") || "") + " / " + (formatDateDDMMYYYY(val("po_date")) || ""));
    rightCell("QAP NO",          val("qap_no", "qap") || "");
    y += rH;

    // TCDS No & Date + END CONN (spans 2 rows)
    const endConnRowSpan = rH * 2;
    drawCell({ x: lx, y, w: LL, h: rH, text: "TCDS NO. & DATE", bold: true, fontSize: 7 });
    drawCell({ x: lx + LL, y, w: LW - LL, h: rH, text: ": " + (val("tcds_no", "section_number") || "") + " DT." + (formatDateDDMMYYYY(val("tcds_date", "tcds_release_date")) || ""), fontSize: 7 });
    drawCell({ x: rx, y, w: RL, h: endConnRowSpan, text: "END CONN.\nSTD. & ED.\nRAISED FACE\nSTD. & ED.", bold: true, fontSize: 6, wrap: true, valign: "top" });
    drawCell({ x: rx + RL, y, w: RW - RL, h: endConnRowSpan, text: ": " + val("end_connection", "end_conn_std") + "\n: " + val("raised_face_std"), fontSize: 7, wrap: true, valign: "top" });
    y += rH;

    drawCell({ x: lx, y, w: LL, h: rH, text: "GAD NO.", bold: true, fontSize: 7 });
    drawCell({ x: lx + LL, y, w: LW - LL, h: rH, text: ": " + val("gad_no", "gad"), fontSize: 7 });
    y += rH;

    // Item Description row
    const iLW = 38, iVW = LW - iLW;
    const tLW = 14, tVW = 28, cLW = 16, cVW = RW - tLW - tVW - cLW;
    
    drawCell({ x: lx, y, w: iLW, h: rH, text: "ITEM DESCRIPTION", bold: true, fontSize: 7 });
    drawCell({ x: lx + iLW, y, w: iVW, h: rH, text: ": " + val("item_description", "type", "section_title"), fontSize: 7 });
    drawCell({ x: rx, y, w: tLW, h: rH, text: "TRIM", bold: true, fontSize: 7 });
    drawCell({ x: rx + tLW, y, w: tVW, h: rH, text: ": " + val("trim"), fontSize: 7 });
    drawCell({ x: rx + tLW + tVW, y, w: cLW, h: rH, text: "CLASS", bold: true, fontSize: 7 });
    drawCell({ x: rx + tLW + tVW + cLW, y, w: cVW, h: rH, text: ": " + val("class"), fontSize: 7 });
    y += rH;

    // Size / QTY / SR NO row
    drawCell({ x: lx, y, w: iLW, h: rH, text: "SIZE", bold: true, fontSize: 7 });
    drawCell({ x: lx + iLW, y, w: iVW, h: rH, text: ": " + val("size"), fontSize: 7 });
    drawCell({ x: rx, y, w: tLW, h: rH, text: "QTY.", bold: true, fontSize: 7 });
    drawCell({ x: rx + tLW, y, w: tVW, h: rH, text: ": " + val("qty", "quantity"), fontSize: 7 });
    drawCell({ x: rx + tLW + tVW, y, w: cLW, h: rH, text: "SR. NO.", bold: true, fontSize: 6.5 });
    drawCell({ x: rx + tLW + tVW + cLW, y, w: cVW, h: rH, text: ": " + val("sr_no"), fontSize: 7 });
    y += rH;

    // ── SECTION HEADERS ───────────────────────────────────────────────────────
    const stdSecW = Math.round(TW * 0.38);
    const tstSecW = TW - stdSecW;
    const stdX = ML, tstX = ML + stdSecW;
    const secH = 6;

    drawCell({ x: stdX, y, w: stdSecW, h: secH, text: "APPLICABLE STANDARDS", bold: true, fontSize: 7.5, align: "center", fill: null });
    drawCell({ x: tstX, y, w: tstSecW, h: secH, text: "TESTING DETAILS", bold: true, fontSize: 7.5, align: "center", fill: null });
    y += secH;

    // Sub-headers
    const dsW = Math.round(stdSecW * 0.37);
    const tsW = Math.round(stdSecW * 0.37);
    const stVW = stdSecW - dsW - tsW;
    const pneumW = Math.round(tstSecW * 0.18);
    const hydroW = tstSecW - pneumW;
    const colW = Math.round(hydroW / 4);
    const subH = 10;

    drawCell({ x: stdX, y, w: dsW, h: subH, text: "DESIGN\nSTANDARD\nand EDITION", bold: true, italic: true, fontSize: 6, align: "center", wrap: true, fill: null });
    drawCell({ x: stdX + dsW, y, w: tsW, h: subH, text: "TESTING\nSTD. AND\nEDITION", bold: true, fontSize: 5.5, align: "center", wrap: true, valign: "middle", fill: null });
    drawCell({ x: stdX + dsW + tsW, y, w: stVW, h: subH, text: val("design_standard") || "Master - Drop Down", fontSize: 6, align: "center", wrap: true });

    // Right: Hydrostatic + Pneumatic headers
    drawCell({ x: tstX, y, w: hydroW, h: subH / 2, text: "HYDROSTATIC TESTING", bold: true, fontSize: 7, align: "center", fill: null });
    drawCell({ x: tstX + hydroW, y, w: pneumW, h: subH, text: "PNEUMATIC\nTESTING", bold: true, fontSize: 5.5, align: "center", wrap: true, valign: "middle", fill: null });
    drawCell({ x: tstX, y: y + subH/2, w: colW, h: subH/2, text: "BACK SEAT", bold: true, fontSize: 5.5, align: "center", fill: null });
    drawCell({ x: tstX + colW, y: y + subH/2, w: colW, h: subH/2, text: "BODY", bold: true, fontSize: 6, align: "center", fill: null });
    drawCell({ x: tstX + colW*2, y: y + subH/2, w: colW, h: subH/2, text: "SEAT", bold: true, fontSize: 6, align: "center", fill: null });
    drawCell({ x: tstX + colW*3, y: y + subH/2, w: hydroW - colW*3, h: subH/2, text: "SEAT", bold: true, fontSize: 6, align: "center", fill: null });
    y += subH;

    // Test data rows
    const testRows = [
      { label: "TEST PRESSURE (Kg/cm2) :", backSeat: val("test_pressure_back_seat") || "Auto fill", body: val("test_pressure_body") || "Auto fill", seat: val("test_pressure_seat") || "Auto fill", pneum: val("test_pressure_pneumatic") || "Auto fill" },
      { label: "TESTING DURATION :", backSeat: val("testing_duration") || "Auto fill", body: val("testing_duration") || "Auto fill", seat: val("testing_duration") || "Auto fill", pneum: val("testing_duration") || "Auto fill" },
      { label: "TEST RESULT :", backSeat: val("test_result") || "Auto fill", body: val("test_result") || "Auto fill", seat: val("test_result") || "Auto fill", pneum: val("test_result") || "Auto fill" },
    ];

    testRows.forEach(({ label, backSeat, body, seat, pneum }) => {
      const trH = 6;
      drawCell({ x: stdX, y, w: stdSecW, h: trH, text: label, bold: true, fontSize: 6.5, align: "right" });
      drawCell({ x: tstX, y, w: colW, h: trH, text: backSeat, fontSize: 6.5, align: "center" });
      drawCell({ x: tstX + colW, y, w: colW, h: trH, text: body, fontSize: 6.5, align: "center" });
      drawCell({ x: tstX + colW*2, y, w: colW, h: trH, text: seat, fontSize: 6.5, align: "center" });
      drawCell({ x: tstX + colW*3, y, w: hydroW - colW*3, h: trH, text: seat, fontSize: 6.5, align: "center" });
      drawCell({ x: tstX + hydroW, y, w: pneumW, h: trH, text: pneum, fontSize: 6.5, align: "center" });
      y += trH;
    });

    // ── MATERIAL TABLE ────────────────────────────────────────────────────────
    const matHH = 9;
    const idW = 22, msW = 34, hnW = 26, mtcCW = 38, remW = TW - idW - msW - hnW - mtcCW;

    drawCell({ x: ML, y, w: idW, h: matHH, text: "ITEM\nDESCRIPTION", bold: true, fontSize: 6.5, align: "center", wrap: true, fill: null });
    drawCell({ x: ML + idW, y, w: msW, h: matHH, text: "MATERIAL\nSPECIFICATION", bold: true, fontSize: 6.5, align: "center", wrap: true, fill: null });
    drawCell({ x: ML + idW + msW, y, w: hnW, h: matHH, text: "HEAT NO.\n/ ID\nNO./LOT NO.", bold: true, fontSize: 5.5, align: "center", wrap: true, valign: "middle", fill: null });
    drawCell({ x: ML + idW + msW + hnW, y, w: mtcCW, h: matHH, text: "MTC NO.", bold: true, fontSize: 6.5, align: "center", fill: null });
    drawCell({ x: ML + idW + msW + hnW + mtcCW, y, w: remW, h: matHH, text: "REMARKS", bold: true, fontSize: 6.5, align: "center", fill: null });
    y += matHH;

    // Material rows for Gate Valve - now with individual Heat No and MTC No for each
    const isHeatAnnexure = Boolean(
  formData.heat_annexure ||
  formData.heatAnnexure ||
  formData.is_heat_annexure ||
  formData.isHeatAnnexure ||
  formData.heat_annexure_attached ||
  formData.attach_heat_annexure ||
  formData.attachHeatAnnexure ||
  (heatAnnexureData && heatAnnexureData.length > 0)  // fallback: if annexure data exists, use it
);
    const matRows = [
      { item: "BODY", spec: val("body_material") || val("body", "Body") || "", heat: isHeatAnnexure ? "Refer Annexure" : (val("body_heat_no") || ""), mtcNo: val("body_mtc_no") || "" },
      { item: "BONNET", spec: val("bonnet_material") || val("bonnet", "Bonnet") || "", heat: isHeatAnnexure ? "Refer Annexure" : (val("bonnet_heat_no") || ""), mtcNo: val("bonnet_mtc_no") || "" },
      { item: "WEDGE", spec: val("wedge_material") || val("wedge", "Wedge") || "", heat: isHeatAnnexure ? "Refer Annexure" : (val("wedge_heat_no") || ""), mtcNo: val("wedge_mtc_no") || "" },
      { item: "SEATRING", spec: val("seatring_material") || val("seat_ring", "Seat Ring") || "", heat: isHeatAnnexure ? "Refer Annexure" : (val("seatring_heat_no") || ""), mtcNo: val("seatring_mtc_no") || "" },
      { item: "STEM", spec: val("stem_material") || val("stem", "Stem") || "", heat: isHeatAnnexure ? "Refer Annexure" : (val("stem_heat_no") || ""), mtcNo: val("stem_mtc_no") || "" },
      { item: "PACKING", spec: val("packing_material") || val("packing", "Packing") || "", heat: isHeatAnnexure ? "Refer Annexure" : (val("packing_heat_no") || ""), mtcNo: val("packing_mtc_no") || "" },
      { item: "GASKET", spec: val("gasket_material") || val("gasket", "Gasket") || "", heat: isHeatAnnexure ? "Refer Annexure" : (val("gasket_heat_no") || ""), mtcNo: val("gasket_mtc_no") || "" },
      { item: "STUD & NUT", spec: val("stud_nut_material") || val("stud_nut", "Stud & Nut") || "", heat: isHeatAnnexure ? "Refer Annexure" : (val("stud_nut_heat_no") || ""), mtcNo: val("stud_nut_mtc_no") || "" },
    ];

    const mrH = 6;
    const custCode = val("customer_item_code") || "";
    const codeIdx = Math.max(0, Math.floor(matRows.length / 2) - 1);

    matRows.forEach(({ item, spec, heat, mtcNo }, i) => {
      drawCell({ x: ML, y, w: idW, h: mrH, text: item, bold: true, fontSize: 7 });
      drawCell({ x: ML + idW, y, w: msW, h: mrH, text: spec, fontSize: 6, wrap: true, valign: "middle" });
      drawCell({ x: ML + idW + msW, y, w: hnW, h: mrH, text: heat, fontSize: 7, align: "center" });
      drawCell({ x: ML + idW + msW + hnW, y, w: mtcCW, h: mrH, text: mtcNo, fontSize: 7 });
      
      let remText = "";
      if (custCode) {
        if (i === codeIdx) remText = "CUSTOMER ITEM CODE :";
        if (i === codeIdx + 1) remText = "'" + custCode + "";
      }
      drawCell({ x: ML + idW + msW + hnW + mtcCW, y, w: remW, h: mrH, text: remText, fontSize: 6, bold: i === codeIdx, wrap: true, valign: "middle" });
      y += mrH;
    });

    // ── STATEMENT OF CONFORMITY ───────────────────────────────────────────────
    const confLW = 30, confBodyW = TW - confLW;
    const confText = val("conformity_statement") || 
      "THE VALVE(S) IS/ARE IN FULL CONFORMANCE WITH API 600 14th ED.2021 ALSO IT IS CONFORM TO THE PO SPECIFICATION & THE DIMENSIONS ARE FOUND WITHIN LIMITS. THE VALVE IS CLEAN AND DRYOUT PROPERLY AFTER TESTING AND BEFORE PACKING.";
    const confLines = doc.splitTextToSize(confText, confBodyW - 3);
    const confH = Math.max(13, confLines.length * 3.5 + 5);

    drawCell({ x: ML, y, w: confLW, h: confH, text: "STATEMENT OF\nCONFORMITY :", bold: true, italic: true, fontSize: 6.5, wrap: true, valign: "top" });
    drawCell({ x: ML + confLW, y, w: confBodyW, h: confH, text: confText, fontSize: 6.5, wrap: true, valign: "top" });
    y += confH;

    // ── SIGNATURES ────────────────────────────────────────────────────────────
    const halfW = TW / 2;
    const sigHH = 6;

    drawCell({ x: ML, y, w: halfW, h: sigHH, text: "PREPARED BY", bold: true, fontSize: 7.5, align: "center", fill: null });
    drawCell({ x: ML + halfW, y, w: halfW, h: sigHH, text: "APPROVED BY", bold: true, fontSize: 7.5, align: "center", fill: null });
    y += sigHH;

    const signH = 20;

    // Draw signature boxes
    drawCell({ x: ML, y, w: halfW, h: signH, text: "", fontSize: 7, valign: "top" });
    drawCell({ x: ML + halfW, y, w: halfW, h: signH, text: "", fontSize: 7, valign: "top" });

     // Add signature images if available
     try {
       const preparedSigDataUrl = val("prepared_by_signature");
       if (preparedSigDataUrl) {
         const format = getImageFormatFromDataUrl(preparedSigDataUrl);
         addImageContained(doc, preparedSigDataUrl, format, ML, y, halfW, signH, 1);
       }
     } catch (err) {
       console.error("Error adding prepared_by signature:", err);
     }

     try {
       const approvedSigDataUrl = val("approved_by_signature");
       if (approvedSigDataUrl) {
         const format = getImageFormatFromDataUrl(approvedSigDataUrl);
         addImageContained(doc, approvedSigDataUrl, format, ML + halfW, y, halfW, signH, 1);
       }
     } catch (err) {
       console.error("Error adding approved_by signature:", err);
     }

    y += signH;

    const nLW = 16, nVW = halfW - nLW;
    drawCell({ x: ML, y, w: nLW, h: 8, text: "Name -", bold: true, fontSize: 7 });
    drawCell({ x: ML + nLW, y, w: nVW, h: 8, text: val("prepared_by_name", "prepared_by") || "drop down - master", fontSize: 7 });
    drawCell({ x: ML + halfW, y, w: nLW, h: 8, text: "Name", bold: true, fontSize: 7 });
    drawCell({ x: ML + halfW + nLW, y, w: nVW, h: 8, text: val("approved_by") || "drop down master", fontSize: 7 });

    // ── ADD HEAT ANNEXURE PAGE WITH ATTACHMENTS IF ATTACHED ──────────────────
    if (heatAnnexureData && heatAnnexureData.length > 0) {
      addHeatAnnexurePage(doc, heatAnnexureData, 'gate', formData, attachmentImages, watermarkBase64Cache);
    }

    // ── SAVE ──────────────────────────────────────────────────────────────────
    const fileName = `ITC_Gate_${val("certificate_no", "tcds_no") || "certificate"}_${new Date().toISOString().split("T")[0]}.pdf`;
    const pdfBytes = doc.output('arraybuffer');
    // Don't call doc.save() here - let main function handle download
    return { fileName, pdfBytes };
  } catch (err) {
    console.error("Error generating Gate Valve PDF:", err);
    throw err;
  }
}

// ══════════════════════════════════════════════════════════════════════════════
// CHECK VALVE PDF - API 598 Format (Image 2)
// ══════════════════════════════════════════════════════════════════════════════════════

async function generateCheckValvePdf(tcdsData, formData, heatAnnexureData = null, attachmentImages = null) {
  try {
    const doc = new jsPDF("portrait", "mm", "a4");
    
    await getWatermarkBase64();
    addWatermarkToPage(doc, watermarkBase64Cache);
    
    const { ML, TW, drawCell, val: valHelper } = createPdfHelpers(doc);
    const val = (...keys) => valHelper(formData, tcdsData, ...keys);
    // First page: content starts below the header/watermark area
    let y = HEADER_HEIGHT;

    // Title
    drawCell({ x: ML, y, w: TW, h: 8, text: "INSPECTION & TEST CERTIFICATE", bold: true, fontSize: 11, align: "center", border: false });
    y += 8;
    drawCell({ x: ML, y, w: TW, h: 6, text: "ACCORDING TO EN 10204 TYPE 3.1", bold: true, fontSize: 9, align: "center", border: false });
    y += 8;

    // Header info - similar to Gate valve but with Check valve specific data
    const LW = Math.round(TW * 0.55), RW = TW - LW;
    const lx = ML, rx = ML + LW;
    const LL = 38, RL = 30, rH = 7;

    const leftCell = (lbl, txt) => {
      drawCell({ x: lx, y, w: LL, h: rH, text: lbl, bold: true, fontSize: 7 });
      drawCell({ x: lx + LL, y, w: LW - LL, h: rH, text: ": " + txt, fontSize: 7 });
    };
    const rightCell = (lbl, txt) => {
      const wrap = lbl.includes("\n");
      drawCell({ x: rx, y, w: RL, h: rH, text: lbl, bold: true, fontSize: wrap ? 6 : 7, wrap, valign: wrap ? "top" : "middle" });
      drawCell({ x: rx + RL, y, w: RW - RL, h: rH, text: ": " + txt, fontSize: 7, wrap, valign: wrap ? "top" : "middle" });
    };

    leftCell("CERTIFICATE NO.", val("certificate_no") || "IPCIPL/TC/25-26/06079");
    rightCell("DATE", formatDateDDMMYYYY(val("date")) || formatDateDDMMYYYY(new Date()));
    y += rH;

    leftCell("CUSTOMER", val("customer", "customer_name") || "");
    rightCell("TAG NO.", val("tag_no") || "");
    y += rH;

    leftCell("P.O. NO. & DATE", (val("po_no", "purchase_order_no") || "") + " / Lots DT." + (formatDateDDMMYYYY(val("po_date")) || ""));
    rightCell("QAP NO", val("qap_no", "qap") || "IPCIPL/QAP/...");
    y += rH;

    leftCell("TCDS NO. & DATE", (val("tcds_no", "section_number") || "") + " DT." + (formatDateDDMMYYYY(val("tcds_date", "tcds_release_date")) || ""));
    rightCell("END CONN.\nSTD. & ED.", val("end_connection") || "");
    y += rH;

    leftCell("GAD NO.", val("gad_no", "gad") || "");
    rightCell("RAISED FACE\nSTD. & ED.", val("raised_face_std") || "");
    y += rH;

    leftCell("ITEM DESCRIPTION", val("item_description", "type") || "LIFT CHECK VALVE");
    rightCell("TRIM", val("trim") || "TR-2 (18-8 18-8)");
    y += rH;

    leftCell("SIZE", val("size") || "");
    rightCell("CLASS", val("class") || "");
    y += rH;

    leftCell("SR. NO.", val("sr_no") || "");
    rightCell("QTY.", (val("qty", "quantity") || "") + " Nos.");
    y += rH;

    // ── APPLICABLE STANDARDS / TESTING DETAILS ────────────────────────────────
    const stdSecW = Math.round(TW * 0.35);
    const tstSecW = TW - stdSecW;
    const stdX = ML, tstX = ML + stdSecW;
    const secH = 6;

    drawCell({ x: stdX, y, w: stdSecW, h: secH, text: "APPLICABLE STANDARDS", bold: true, fontSize: 7.5, align: "center", fill: null });
    drawCell({ x: tstX, y, w: tstSecW, h: secH, text: "TESTING DETAILS", bold: true, fontSize: 7.5, align: "center", fill: null });
    y += secH;

    // Sub-headers
    const stdColW1 = Math.round(stdSecW / 4);
    const stdColW2 = Math.round(stdSecW / 4);
    const stdColW3 = Math.round(stdSecW / 4);
    const stdColW4 = stdSecW - stdColW1 - stdColW2 - stdColW3;
    const pneumW = Math.round(tstSecW * 0.18);
    const hydroW = tstSecW - pneumW;
    const colW = Math.round(hydroW / 3);
    const subH = 10;

    drawCell({ x: stdX, y, w: stdColW1, h: subH, text: "DESIGN\nSTANDARD and EDITION", bold: true, fontSize: 6, align: "center", wrap: true, fill: null });
    drawCell({ x: stdX + stdColW1, y, w: stdColW2, h: subH, text: val("design_standard", "design_std") || "BS 1868\nED. 1990", fontSize: 7, align: "center", wrap: true });
    drawCell({ x: stdX + stdColW1 + stdColW2, y, w: stdColW3, h: subH, text: "TESTING\nSTD. AND\nEDITION", bold: true, fontSize: 6, align: "center", wrap: true, fill: null });
    drawCell({ x: stdX + stdColW1 + stdColW2 + stdColW3, y, w: stdColW4, h: subH, text: val("testing_std", "testing_standard") || "API 598\n11th ED. 2023", fontSize: 7, align: "center", wrap: true });

    drawCell({ x: tstX, y, w: hydroW, h: subH / 2, text: "HYDROSTATIC TESTING", bold: true, fontSize: 7, align: "center", fill: null });
    drawCell({ x: tstX + hydroW, y, w: pneumW, h: subH, text: "PNEUMATIC\nTESTING\nSEAT", bold: true, fontSize: 5.5, align: "center", wrap: true, valign: "middle", fill: null });
    drawCell({ x: tstX, y: y + subH / 2, w: colW, h: subH / 2, text: "BACK SEAT", bold: true, fontSize: 6, align: "center", fill: null });
    drawCell({ x: tstX + colW, y: y + subH / 2, w: colW, h: subH / 2, text: "BODY", bold: true, fontSize: 6, align: "center", fill: null });
    drawCell({ x: tstX + colW * 2, y: y + subH / 2, w: hydroW - colW * 2, h: subH / 2, text: "SEAT", bold: true, fontSize: 6, align: "center", fill: null });
    y += subH;

    // Test data rows
    const testRows = [
      { label: "TEST PRESSURE (Kg/cm2) :", backSeat: "NA", body: val("test_pressure_body") || "30 Kg/cm²", seat: val("test_pressure_seat") || "22 Kg/cm²", pneum: "NA" },
      { label: "TESTING DURATION :", backSeat: "NA", body: val("testing_duration") || "15 Sec", seat: val("testing_duration") || "15 Sec", pneum: "NA" },
      { label: "TEST RESULT :", backSeat: "NA", body: "NO VISIBLE\nLEAKAGE\nFOUND", seat: "FOUND\nWITHIN\nALLOWABLE\nLEAKAGE", pneum: "NA" },
    ];

    testRows.forEach(({ label, backSeat, body, seat, pneum }) => {
      const trH = label.includes("RESULT") ? 12 : 6;
      drawCell({ x: stdX, y, w: stdSecW, h: trH, text: label, bold: true, fontSize: 6.5, align: "right" });
      drawCell({ x: tstX, y, w: colW, h: trH, text: backSeat, fontSize: 6, align: "center", wrap: true, valign: "middle" });
      drawCell({ x: tstX + colW, y, w: colW, h: trH, text: body, fontSize: 6, align: "center", wrap: true, valign: "middle" });
      drawCell({ x: tstX + colW*2, y, w: hydroW - colW*2, h: trH, text: seat, fontSize: 6, align: "center", wrap: true, valign: "middle" });
      drawCell({ x: tstX + hydroW, y, w: pneumW, h: trH, text: pneum, fontSize: 6, align: "center", wrap: true, valign: "middle" });
      y += trH;
    });

    // ── MATERIAL TABLE (Check Valve specific) ─────────────────────────────────
    const matHH = 9;
    const idW = 22, msW = 38, hnW = 28, mtcCW = 42, remW = TW - idW - msW - hnW - mtcCW;

    drawCell({ x: ML, y, w: idW, h: matHH, text: "ITEM\nDESCRIPTION", bold: true, fontSize: 6.5, align: "center", wrap: true, fill: null });
    drawCell({ x: ML + idW, y, w: msW, h: matHH, text: "MATERIAL SPECIFICATION", bold: true, fontSize: 6.5, align: "center", wrap: true, fill: null });
    drawCell({ x: ML + idW + msW, y, w: hnW, h: matHH, text: "HEAT NO. / ID\nNO./LOT NO.", bold: true, fontSize: 5.5, align: "center", wrap: true, valign: "middle", fill: null });
    drawCell({ x: ML + idW + msW + hnW, y, w: mtcCW, h: matHH, text: "MTC NO.", bold: true, fontSize: 6.5, align: "center", fill: null });
    drawCell({ x: ML + idW + msW + hnW + mtcCW, y, w: remW, h: matHH, text: "REMARKS", bold: true, fontSize: 6.5, align: "center", fill: null });
    y += matHH;

    // Material rows for Check Valve - with individual Heat No and MTC No for each
    const isHeatAnnexure = Boolean(
  formData.heat_annexure ||
  formData.heatAnnexure ||
  formData.is_heat_annexure ||
  formData.isHeatAnnexure ||
  formData.heat_annexure_attached ||
  formData.attach_heat_annexure ||
  formData.attachHeatAnnexure ||
  (heatAnnexureData && heatAnnexureData.length > 0)  // fallback: if annexure data exists, use it
);
    const matRows = [
      { item: "BODY", spec: val("body_material") || val("body", "Body") || "", heat: isHeatAnnexure ? "Refer Annexure" : (val("body_heat_no") || ""), mtcNo: val("body_mtc_no") || "" },
      { item: "COVER", spec: val("cover_material") || val("cover", "Cover") || "", heat: isHeatAnnexure ? "Refer Annexure" : (val("cover_heat_no") || ""), mtcNo: val("cover_mtc_no") || "" },
      { item: "PLUG", spec: val("plug_material") || val("plug", "Plug") || "", heat: isHeatAnnexure ? "Refer Annexure" : (val("plug_heat_no") || ""), mtcNo: val("plug_mtc_no") || "" },
      { item: "GASKET", spec: val("gasket_material") || val("gasket", "Gasket") || "", heat: isHeatAnnexure ? "Refer Annexure" : (val("gasket_heat_no") || ""), mtcNo: val("gasket_mtc_no") || "" },
      { item: "HEX BOLT", spec: val("hex_bolt_material") || val("hex_bolt", "Hex Bolt") || "", heat: isHeatAnnexure ? "Refer Annexure" : (val("hex_bolt_heat_no") || ""), mtcNo: val("hex_bolt_mtc_no") || "" },
    ];

    const mrH = 6;
    const custCode = val("customer_item_code") || "";

    matRows.forEach(({ item, spec, heat, mtcNo }, i) => {
      drawCell({ x: ML, y, w: idW, h: mrH, text: item, bold: true, fontSize: 7 });
      drawCell({ x: ML + idW, y, w: msW, h: mrH, text: spec, fontSize: 6, wrap: true, valign: "middle" });
      drawCell({ x: ML + idW + msW, y, w: hnW, h: mrH, text: heat, fontSize: 7, align: "center" });
      drawCell({ x: ML + idW + msW + hnW, y, w: mtcCW, h: mrH, text: mtcNo, fontSize: 6.5, wrap: true });
      
      let remText = "";
      if (i === 2 && custCode) remText = "CUSTOMER ITEM CODE - " + custCode;
      drawCell({ x: ML + idW + msW + hnW + mtcCW, y, w: remW, h: mrH, text: remText, fontSize: 6, wrap: true, valign: "middle" });
      y += mrH;
    });

    // ── STATEMENT OF CONFORMITY ───────────────────────────────────────────────
    const confLW = 30, confBodyW = TW - confLW;
    const confText = val("conformity_statement") || 
      "THE VALVE(S) IS/ARE IN FULL CONFORMANCE WITH BS 1868 ED. 1990 ALSO IT IS CONFORM TO THE PO SPECIFICATION & THE DIMENSIONS ARE FOUND WITHIN LIMITS. THE VALVE IS CLEAN AND DRYOUT PROPERLY AFTER TESTING AND BEFORE PACKING.";
    const confLines = doc.splitTextToSize(confText, confBodyW - 3);
    const confH = Math.max(13, confLines.length * 3.5 + 5);

    drawCell({ x: ML, y, w: confLW, h: confH, text: "STATEMENT OF\nCONFORMITY :", bold: true, italic: true, fontSize: 6.5, wrap: true, valign: "top" });
    drawCell({ x: ML + confLW, y, w: confBodyW, h: confH, text: confText, fontSize: 6.5, wrap: true, valign: "top" });
    y += confH;

    // ── SIGNATURES ────────────────────────────────────────────────────────────
    const halfW = TW / 2;
    const sigHH = 6;

    drawCell({ x: ML, y, w: halfW, h: sigHH, text: "PREPARED BY", bold: true, fontSize: 7.5, align: "center", fill: null });
    drawCell({ x: ML + halfW, y, w: halfW, h: sigHH, text: "APPROVED BY", bold: true, fontSize: 7.5, align: "center", fill: null });
    y += sigHH;

    const signH = 18;

    // Draw signature boxes
    drawCell({ x: ML, y, w: halfW, h: signH, text: "", fontSize: 7, valign: "top" });
    drawCell({ x: ML + halfW, y, w: halfW, h: signH, text: "", fontSize: 7, valign: "top" });

     // Add signature images if available
     try {
       const preparedSigDataUrl = val("prepared_by_signature");
       if (preparedSigDataUrl) {
         const format = getImageFormatFromDataUrl(preparedSigDataUrl);
         addImageContained(doc, preparedSigDataUrl, format, ML, y, halfW, signH, 1);
       }
     } catch (err) {
       console.error("Error adding prepared_by signature (check):", err);
     }

     try {
       const approvedSigDataUrl = val("approved_by_signature");
       if (approvedSigDataUrl) {
         const format = getImageFormatFromDataUrl(approvedSigDataUrl);
         addImageContained(doc, approvedSigDataUrl, format, ML + halfW, y, halfW, signH, 1);
       }
     } catch (err) {
       console.error("Error adding approved_by signature (check):", err);
     }

    y += signH;

    const nLW = 16, nVW = halfW - nLW;
    drawCell({ x: ML, y, w: nLW, h: 8, text: "Name -", bold: true, fontSize: 7 });
    drawCell({ x: ML + nLW, y, w: nVW, h: 8, text: val("prepared_by_name", "prepared_by") || "Shubham D. Jagnade", fontSize: 7 });
    drawCell({ x: ML + halfW, y, w: nLW, h: 8, text: "Name", bold: true, fontSize: 7 });
    drawCell({ x: ML + halfW + nLW, y, w: nVW, h: 8, text: val("approved_by") || "Vaman K. Randave", fontSize: 7 });

    // ── ADD HEAT ANNEXURE PAGE WITH ATTACHMENTS IF ATTACHED ──────────────────
    if (heatAnnexureData && heatAnnexureData.length > 0) {
      addHeatAnnexurePage(doc, heatAnnexureData, 'check', formData, attachmentImages, watermarkBase64Cache);
    }

    const fileName = `ITC_Check_${val("certificate_no", "tcds_no") || "certificate"}_${new Date().toISOString().split("T")[0]}.pdf`;
    const pdfBytes = doc.output('arraybuffer');
    return { fileName, pdfBytes };
  } catch (err) {
    console.error("Error generating Check Valve PDF:", err);
    throw err;
  }
}

// ══════════════════════════════════════════════════════════════════════════════
// GLOBE VALVE PDF - ISO 15761 Format (Image 3)
// ══════════════════════════════════════════════════════════════════════════════

async function generateGlobeValvePdf(tcdsData, formData, heatAnnexureData = null, attachmentImages = null) {
  try {
    const doc = new jsPDF("portrait", "mm", "a4");
    
    await getWatermarkBase64();
    addWatermarkToPage(doc, watermarkBase64Cache);
    
    const { ML, TW, drawCell, val: valHelper } = createPdfHelpers(doc);
    const val = (...keys) => valHelper(formData, tcdsData, ...keys);
    // First page: content starts below the header/watermark area
    let y = HEADER_HEIGHT;

    // Title
    drawCell({ x: ML, y, w: TW, h: 8, text: "INSPECTION & TEST CERTIFICATE", bold: true, fontSize: 11, align: "center", border: false });
    y += 8;
    drawCell({ x: ML, y, w: TW, h: 6, text: "ACCORDING TO EN 10204 TYPE 3.1", bold: true, fontSize: 9, align: "center", border: false });
    y += 8;

    // Header info - Globe valve specific
    const LW = Math.round(TW * 0.55), RW = TW - LW;
    const lx = ML, rx = ML + LW;
    const LL = 38, RL = 30, rH = 7;

    const leftCell = (lbl, txt) => {
      drawCell({ x: lx, y, w: LL, h: rH, text: lbl, bold: true, fontSize: 7 });
      drawCell({ x: lx + LL, y, w: LW - LL, h: rH, text: ": " + txt, fontSize: 7 });
    };
    const rightCell = (lbl, txt) => {
      drawCell({ x: rx, y, w: RL, h: rH, text: lbl, bold: true, fontSize: 7 });
      drawCell({ x: rx + RL, y, w: RW - RL, h: rH, text: ": " + txt, fontSize: 7 });
    };

    leftCell("CERTIFICATE NO.", val("certificate_no") || "IPCIPL/TC/25-26/06182");
    rightCell("DATE", formatDateDDMMYYYY(val("date")) || formatDateDDMMYYYY(new Date()));
    y += rH;

    leftCell("CUSTOMER", val("customer", "customer_name") || "");
    rightCell("TAG NO.", val("tag_no") || "NA");
    y += rH;

    leftCell("P.O. NO. & DATE", (val("po_no", "purchase_order_no") || "") + " DT." + (formatDateDDMMYYYY(val("po_date")) || ""));
    rightCell("QAP NO", val("qap_no", "qap") || "IPCIPL/QAP/MU2451441");
    y += rH;

    // TCDS + END CONN
    const endConnRowSpan = rH * 2;
    drawCell({ x: lx, y, w: LL, h: rH, text: "TCDS NO. & DATE", bold: true, fontSize: 7 });
    drawCell({ x: lx + LL, y, w: LW - LL, h: rH, text: ": " + val("tcds_no") + " DT." + formatDateDDMMYYYY(val("tcds_date")), fontSize: 7 });
    drawCell({ x: rx, y, w: RL, h: endConnRowSpan, text: "END CONN.\nSTD. & ED.\nRAISED FACE\nSTD. & ED.", bold: true, fontSize: 6, wrap: true, valign: "top" });
    drawCell({ x: rx + RL, y, w: RW - RL, h: endConnRowSpan, text: ": " + val("end_connection") + "\n: " + val("raised_face_std"), fontSize: 7, wrap: true, valign: "top" });
    y += rH;

    drawCell({ x: lx, y, w: LL, h: rH, text: "GAD NO.", bold: true, fontSize: 7 });
    drawCell({ x: lx + LL, y, w: LW - LL, h: rH, text: ": " + val("gad_no", "gad"), fontSize: 7 });
    y += rH;

    leftCell("ITEM DESCRIPTION", val("item_description", "type") || "GLOBE VALVE");
    rightCell("TRIM", val("trim") || "TR-10");
    y += rH;

    // Size / Class / QTY row
    const sLW = 30, sVW = 30, cLW = 20, cVW = 20, qLW = 20, qVW = TW - sLW - sVW - cLW - cVW - qLW;
    drawCell({ x: ML, y, w: sLW, h: rH, text: "SIZE", bold: true, fontSize: 7 });
    drawCell({ x: ML + sLW, y, w: sVW, h: rH, text: ": " + val("size"), fontSize: 7 });
    drawCell({ x: ML + sLW + sVW, y, w: cLW, h: rH, text: "CLASS", bold: true, fontSize: 7 });
    drawCell({ x: ML + sLW + sVW + cLW, y, w: cVW, h: rH, text: ": #" + val("class"), fontSize: 7 });
    drawCell({ x: ML + sLW + sVW + cLW + cVW, y, w: qLW, h: rH, text: "QTY.", bold: true, fontSize: 7 });
    drawCell({ x: ML + sLW + sVW + cLW + cVW + qLW, y, w: qVW, h: rH, text: ": " + val("qty", "quantity") + " No.", fontSize: 7 });
    y += rH;

    leftCell("SR. NO.", val("sr_no") || "");
    y += rH;

    // ── APPLICABLE STANDARDS / TESTING DETAILS ────────────────────────────────
    const stdSecW = Math.round(TW * 0.35);
    const tstSecW = TW - stdSecW;
    const stdX = ML, tstX = ML + stdSecW;
    const secH = 6;

    drawCell({ x: stdX, y, w: stdSecW, h: secH, text: "APPLICABLE STANDARDS", bold: true, fontSize: 7.5, align: "center", fill: null });
    drawCell({ x: tstX, y, w: tstSecW, h: secH, text: "TESTING DETAILS", bold: true, fontSize: 7.5, align: "center", fill: null });
    y += secH;

    // Sub-headers - ISO 15761 specific
    const dsW = Math.round(stdSecW / 2);
    const tsW = stdSecW - dsW;
    const pneumW = Math.round(tstSecW * 0.18);
    const hydroW = tstSecW - pneumW;
    const colW = Math.round(hydroW / 3);
    const subH = 10;

    drawCell({ x: stdX, y, w: dsW, h: subH, text: "DESIGN\nSTANDARD and\nEDITION", bold: true, fontSize: 6, align: "center", wrap: true, fill: null });
    drawCell({ x: stdX + dsW, y, w: tsW, h: subH, text: "TESTING\nSTD. AND\nEDITION", bold: true, fontSize: 6, align: "center", wrap: true, fill: null });

    drawCell({ x: tstX, y, w: hydroW, h: subH / 2, text: "HYDROSTATIC TESTING", bold: true, fontSize: 7, align: "center", fill: null });
    drawCell({ x: tstX + hydroW, y, w: pneumW, h: subH, text: "PNEUMATIC\nTESTING\nSEAT", bold: true, fontSize: 5.5, align: "center", wrap: true, valign: "middle", fill: null });
    drawCell({ x: tstX, y: y + subH/2, w: colW, h: subH/2, text: "BACK SEAT", bold: true, fontSize: 6, align: "center", fill: null });
    drawCell({ x: tstX + colW, y: y + subH/2, w: colW, h: subH/2, text: "BODY", bold: true, fontSize: 6, align: "center", fill: null });
    drawCell({ x: tstX + colW*2, y: y + subH/2, w: hydroW - colW*2, h: subH/2, text: "SEAT", bold: true, fontSize: 6, align: "center", fill: null });
    y += subH;

    // Standard values row - ISO 15761 ED. 2020
    drawCell({ x: stdX, y, w: dsW, h: 8, text: val("design_standard") || "ISO 15761\nED. 2020", fontSize: 7, align: "center", wrap: true });
    drawCell({ x: stdX + dsW, y, w: tsW, h: 8, text: val("testing_std") || "API 598\n11th ED. 2023", fontSize: 7, align: "center", wrap: true });
    drawCell({ x: tstX, y, w: colW, h: 8, text: "", fontSize: 7, align: "center" });
    drawCell({ x: tstX + colW, y, w: colW, h: 8, text: "", fontSize: 7, align: "center" });
    drawCell({ x: tstX + colW*2, y, w: hydroW - colW*2, h: 8, text: "", fontSize: 7, align: "center" });
    drawCell({ x: tstX + hydroW, y, w: pneumW, h: 8, text: "", fontSize: 7, align: "center" });
    y += 8;

    // Test data rows
    const testRows = [
      { label: "TEST PRESSURE (kg/cm2) :", backSeat: val("test_pressure_back_seat") || "150 Kg/cm²", body: val("test_pressure_body") || "204 Kg/cm²", seat: val("test_pressure_seat") || "150 Kg/cm²", pneum: val("test_pressure_pneumatic") || "6-7 Kg/cm²" },
      { label: "TESTING DURATION :", backSeat: val("testing_duration") || "15 Sec", body: val("testing_duration") || "15 Sec", seat: val("testing_duration") || "15 Sec", pneum: "15 Sec" },
      { label: "TEST RESULT :", backSeat: "NO VISIBLE\nLEAKAGE FOUND", body: "NO VISIBLE\nLEAKAGE FOUND", seat: "NO VISIBLE\nLEAKAGE FOUND", pneum: "NO VISIBLE\nLEAKAGE FOUND" },
    ];

    testRows.forEach(({ label, backSeat, body, seat, pneum }) => {
      const trH = label.includes("RESULT") ? 12 : 6;
      drawCell({ x: stdX, y, w: stdSecW, h: trH, text: label, bold: true, fontSize: 6.5, align: "right" });
      drawCell({ x: tstX, y, w: colW, h: trH, text: backSeat, fontSize: 6, align: "center", wrap: true, valign: "middle" });
      drawCell({ x: tstX + colW, y, w: colW, h: trH, text: body, fontSize: 6, align: "center", wrap: true, valign: "middle" });
      drawCell({ x: tstX + colW*2, y, w: hydroW - colW*2, h: trH, text: seat, fontSize: 6, align: "center", wrap: true, valign: "middle" });
      drawCell({ x: tstX + hydroW, y, w: pneumW, h: trH, text: pneum, fontSize: 6, align: "center", wrap: true, valign: "middle" });
      y += trH;
    });

    // ── MATERIAL TABLE (Globe Valve specific - from Image 3) ──────────────────
    const matHH = 9;
    const idW = 28, msW = 36, hnW = 26, mtcCW = 40, remW = TW - idW - msW - hnW - mtcCW;

    drawCell({ x: ML, y, w: idW, h: matHH, text: "ITEM\nDESCRIPTION", bold: true, fontSize: 6.5, align: "center", wrap: true, fill: null });
    drawCell({ x: ML + idW, y, w: msW, h: matHH, text: "MATERIAL SPECIFICATION", bold: true, fontSize: 6.5, align: "center", wrap: true, fill: null });
    drawCell({ x: ML + idW + msW, y, w: hnW, h: matHH, text: "HEAT NO. / ID\nNO./LOT NO.", bold: true, fontSize: 5.5, align: "center", wrap: true, valign: "middle", fill: null });
    drawCell({ x: ML + idW + msW + hnW, y, w: mtcCW, h: matHH, text: "MTC NO.", bold: true, fontSize: 6.5, align: "center", fill: null });
    drawCell({ x: ML + idW + msW + hnW + mtcCW, y, w: remW, h: matHH, text: "REMARKS", bold: true, fontSize: 6.5, align: "center", fill: null });
    y += matHH;

    // Material rows for Globe Valve - with individual Heat No and MTC No for each
    const isHeatAnnexure = Boolean(
  formData.heat_annexure ||
  formData.heatAnnexure ||
  formData.is_heat_annexure ||
  formData.isHeatAnnexure ||
  formData.heat_annexure_attached ||
  formData.attach_heat_annexure ||
  formData.attachHeatAnnexure ||
  (heatAnnexureData && heatAnnexureData.length > 0)  // fallback: if annexure data exists, use it
);
    const matRows = [
      { item: "BODY", spec: val("body_material") || val("body", "Body") || "", heat: isHeatAnnexure ? "Refer Annexure" : (val("body_heat_no") || ""), mtcNo: val("body_mtc_no") || "" },
      { item: "BONNET", spec: val("bonnet_material") || val("bonnet", "Bonnet") || "", heat: isHeatAnnexure ? "Refer Annexure" : (val("bonnet_heat_no") || ""), mtcNo: val("bonnet_mtc_no") || "" },
      { item: "PLUG", spec: val("plug_material") || val("plug", "Plug") || "", heat: isHeatAnnexure ? "Refer Annexure" : (val("plug_heat_no") || ""), mtcNo: val("plug_mtc_no") || "" },
      { item: "SEAT RING (Integral)", spec: val("seat_ring_material") || val("seat_ring", "Seat Ring") || "", heat: isHeatAnnexure ? "Refer Annexure" : (val("seat_ring_heat_no") || ""), mtcNo: val("seat_ring_mtc_no") || "" },
      { item: "STEM", spec: val("stem_material") || val("stem", "Stem") || "", heat: isHeatAnnexure ? "Refer Annexure" : (val("stem_heat_no") || ""), mtcNo: val("stem_mtc_no") || "" },
      { item: "GASKET", spec: val("gasket_material") || val("gasket", "Gasket") || "", heat: isHeatAnnexure ? "Refer Annexure" : (val("gasket_heat_no") || ""), mtcNo: val("gasket_mtc_no") || "" },
      { item: "GLAND PACKING", spec: val("gland_packing_material") || val("gland_packing", "Gland Packing") || "", heat: isHeatAnnexure ? "Refer Annexure" : (val("gland_packing_heat_no") || ""), mtcNo: val("gland_packing_mtc_no") || "" },
      { item: "HEX BOLT", spec: val("hex_bolt_material") || val("hex_bolt", "Hex Bolt") || "", heat: isHeatAnnexure ? "Refer Annexure" : (val("hex_bolt_heat_no") || ""), mtcNo: val("hex_bolt_mtc_no") || "" },
    ];

    const mrH = 7;
    matRows.forEach(({ item, spec, heat, mtcNo }) => {
      drawCell({ x: ML, y, w: idW, h: mrH, text: item, bold: true, fontSize: 6.5 });
      drawCell({ x: ML + idW, y, w: msW, h: mrH, text: spec, fontSize: 6, wrap: true, valign: "middle" });
      drawCell({ x: ML + idW + msW, y, w: hnW, h: mrH, text: heat, fontSize: 6.5, align: "center" });
      drawCell({ x: ML + idW + msW + hnW, y, w: mtcCW, h: mrH, text: mtcNo, fontSize: 6, wrap: true, valign: "middle" });
      drawCell({ x: ML + idW + msW + hnW + mtcCW, y, w: remW, h: mrH, text: "", fontSize: 6 });
      y += mrH;
    });

    // ── STATEMENT OF CONFORMITY ───────────────────────────────────────────────
    const confLW = 30, confBodyW = TW - confLW;
    const confText = val("conformity_statement") || 
      "THE VALVE(S) IS/ARE IN FULL CONFORMANCE WITH ISO 15761 ED. 2020 ALSO IT IS CONFORM TO THE PO SPECIFICATION & THE DIMENSIONS ARE FOUND WITHIN LIMITS. THE VALVE IS CLEAN AND DRYOUT PROPERLY AFTER TESTING AND BEFORE PACKING.";
    const confLines = doc.splitTextToSize(confText, confBodyW - 3);
    const confH = Math.max(13, confLines.length * 3.5 + 5);

    drawCell({ x: ML, y, w: confLW, h: confH, text: "STATEMENT OF\nCONFORMITY :", bold: true, italic: true, fontSize: 6.5, wrap: true, valign: "top" });
    drawCell({ x: ML + confLW, y, w: confBodyW, h: confH, text: confText, fontSize: 6.5, wrap: true, valign: "top" });
    y += confH;

    // ── SIGNATURES ────────────────────────────────────────────────────────────
    const halfW = TW / 2;
    const sigHH = 6;

    drawCell({ x: ML, y, w: halfW, h: sigHH, text: "PREPARED BY", bold: true, fontSize: 7.5, align: "center", fill: null });
    drawCell({ x: ML + halfW, y, w: halfW, h: sigHH, text: "APPROVED BY", bold: true, fontSize: 7.5, align: "center", fill: null });
    y += sigHH;

    const signH = 18;

    // Draw signature boxes
    drawCell({ x: ML, y, w: halfW, h: signH, text: "", fontSize: 7, valign: "top" });
    drawCell({ x: ML + halfW, y, w: halfW, h: signH, text: "", fontSize: 7, valign: "top" });

     // Add signature images if available
     try {
       const preparedSigDataUrl = val("prepared_by_signature");
       if (preparedSigDataUrl) {
         const format = getImageFormatFromDataUrl(preparedSigDataUrl);
         addImageContained(doc, preparedSigDataUrl, format, ML, y, halfW, signH, 1);
       }
     } catch (err) {
       console.error("Error adding prepared_by signature (globe):", err);
     }

     try {
       const approvedSigDataUrl = val("approved_by_signature");
       if (approvedSigDataUrl) {
         const format = getImageFormatFromDataUrl(approvedSigDataUrl);
         addImageContained(doc, approvedSigDataUrl, format, ML + halfW, y, halfW, signH, 1);
       }
     } catch (err) {
       console.error("Error adding approved_by signature (globe):", err);
     }

    y += signH;

    const nLW = 16, nVW = halfW - nLW;
    drawCell({ x: ML, y, w: nLW, h: 8, text: "Name -", bold: true, fontSize: 7 });
    drawCell({ x: ML + nLW, y, w: nVW, h: 8, text: val("prepared_by_name", "prepared_by") || "Shubham D. Jagnade", fontSize: 7 });
    drawCell({ x: ML + halfW, y, w: nLW, h: 8, text: "Name", bold: true, fontSize: 7 });
    drawCell({ x: ML + halfW + nLW, y, w: nVW, h: 8, text: val("approved_by") || "Vaman K. Randave", fontSize: 7 });

    // ── ADD HEAT ANNEXURE PAGE WITH ATTACHMENTS IF ATTACHED ──────────────────
    if (heatAnnexureData && heatAnnexureData.length > 0) {
      addHeatAnnexurePage(doc, heatAnnexureData, 'globe', formData, attachmentImages, watermarkBase64Cache);
    }

    const fileName = `ITC_Globe_${val("certificate_no", "tcds_no") || "certificate"}_${new Date().toISOString().split("T")[0]}.pdf`;
    const pdfBytes = doc.output('arraybuffer');
    return { fileName, pdfBytes };
  } catch (err) {
    console.error("Error generating Globe Valve PDF:", err);
    throw err;
  }
}

// ══════════════════════════════════════════════════════════════════════════════
// BALL VALVE PDF - Compliance Certificate Format (Image 4)
// ══════════════════════════════════════════════════════════════════════════════

async function generateBallValvePdf(tcdsData, formData, heatAnnexureData = null, attachmentImages = null) {
  try {
    const doc = new jsPDF("portrait", "mm", "a4");
    
    await getWatermarkBase64();
    addWatermarkToPage(doc, watermarkBase64Cache);
    
    const { ML, TW, drawCell, val: valHelper } = createPdfHelpers(doc);
    const val = (...keys) => valHelper(formData, tcdsData, ...keys);
    // First page: content starts below the header/watermark area
    let y = HEADER_HEIGHT;

    // Title - Ball valve uses "COMPLIANCE CERTIFICATE"
    drawCell({ x: ML, y, w: TW, h: 10, text: "COMPLIANCE CERTIFICATE", bold: true, fontSize: 14, align: "center", border: false });
    y += 10;
    drawCell({ x: ML, y, w: TW, h: 6, text: "CERTIFICATION AS PER EN 10204 3.1", bold: false, fontSize: 8, align: "center", border: false });
    y += 8;

    // Header row 1: Certificate No, Date
    const col1W = 35, col2W = 45, col3W = 25, col4W = 30, col5W = TW - col1W - col2W - col3W - col4W;
    const rH = 7;

    drawCell({ x: ML, y, w: col1W, h: rH, text: "CERTIFICATE NO:", bold: true, fontSize: 8 });
    drawCell({ x: ML + col1W, y, w: col2W, h: rH, text: val("certificate_no") || "FB4020083", fontSize: 8 });
    drawCell({ x: ML + col1W + col2W, y, w: col3W, h: rH, text: "DATE:", bold: true, fontSize: 8 });
    drawCell({ x: ML + col1W + col2W + col3W, y, w: col4W + col5W, h: rH, text: formatDateDDMMYYYY(val("date")) || formatDateDDMMYYYY(new Date()), fontSize: 8 });
    y += rH;

    // Customer row
    drawCell({ x: ML, y, w: col1W, h: rH, text: "CUSTOMER:", bold: true, fontSize: 8 });
    drawCell({ x: ML + col1W, y, w: TW - col1W, h: rH, text: val("customer", "customer_name") || "INTRGRAL PROCESS CONTROLS INDIA PVT LTD. (UNIT 1)", fontSize: 8 });
    y += rH;

    // Valve SR No, Batch No, Description, Size, Class, QTY
    const hdrH = 8;
    drawCell({ x: ML, y, w: 30, h: hdrH, text: "VALVE SR NO:", bold: true, fontSize: 7, fill: null });
    drawCell({ x: ML + 30, y, w: 30, h: hdrH, text: "BATCH NO", bold: true, fontSize: 7, align: "center", fill: null });
    drawCell({ x: ML + 60, y, w: 40, h: hdrH, text: "DESCIRPTION", bold: true, fontSize: 7, align: "center", fill: null });
    drawCell({ x: ML + 100, y, w: 25, h: hdrH, text: "SIZE", bold: true, fontSize: 7, align: "center", fill: null });
    drawCell({ x: ML + 125, y, w: 25, h: hdrH, text: "CLASS", bold: true, fontSize: 7, align: "center", fill: null });
    drawCell({ x: ML + 150, y, w: TW - 150, h: hdrH, text: "QTY", bold: true, fontSize: 7, align: "center", fill: null });
    y += hdrH;

    // Values row
    drawCell({ x: ML, y, w: 30, h: rH, text: val("valve_sr_no", "sr_no") || "1 TO 30", fontSize: 7 });
    drawCell({ x: ML + 30, y, w: 30, h: rH, text: val("batch_no") || "FB4020083", fontSize: 7, align: "center" });
    drawCell({ x: ML + 60, y, w: 40, h: rH, text: val("description", "type") || "SOFT SEATED\nBALL VALVE", fontSize: 6.5, align: "center", wrap: true });
    drawCell({ x: ML + 100, y, w: 25, h: rH, text: val("size") || "40MM", fontSize: 7, align: "center" });
    drawCell({ x: ML + 125, y, w: 25, h: rH, text: "#" + val("class") || "#150", fontSize: 7, align: "center" });
    drawCell({ x: ML + 150, y, w: TW - 150, h: rH, text: val("qty", "quantity") + " NOS." || "30 NOS.", fontSize: 7, align: "center" });
    y += rH + 2;

    // ── MATERIAL OF CONSTRUCTION ──────────────────────────────────────────────
    drawCell({ x: ML, y, w: TW, h: 7, text: "MATERIAL OF CONSTRUCTION", bold: true, fontSize: 9, align: "center", fill: null });
    y += 7;

    // Material headers
    const mColW = TW / 6;
    drawCell({ x: ML, y, w: mColW, h: 7, text: "BODY & B/A", bold: true, fontSize: 7, align: "center", fill: null });
    drawCell({ x: ML + mColW, y, w: mColW, h: 7, text: "BALL", bold: true, fontSize: 7, align: "center", fill: null });
    drawCell({ x: ML + mColW*2, y, w: mColW, h: 7, text: "STEM", bold: true, fontSize: 7, align: "center", fill: null });
    drawCell({ x: ML + mColW*3, y, w: mColW, h: 7, text: "SEAT", bold: true, fontSize: 7, align: "center", fill: null });
    drawCell({ x: ML + mColW*4, y, w: mColW, h: 7, text: "SEAL", bold: true, fontSize: 7, align: "center", fill: null });
    drawCell({ x: ML + mColW*5, y, w: mColW, h: 7, text: "FASTNERS", bold: true, fontSize: 7, align: "center", fill: null });
    y += 7;

    // Material values
    drawCell({ x: ML, y, w: mColW, h: 10, text: val("body_ba", "body") || "ASTM A351/A351M-\n2013A Gr. CF8M", fontSize: 6, align: "center", wrap: true });
    drawCell({ x: ML + mColW, y, w: mColW, h: 10, text: val("ball") || "ASTM A 351\nGR. CF8M", fontSize: 6, align: "center", wrap: true });
    drawCell({ x: ML + mColW*2, y, w: mColW, h: 10, text: val("stem") || "ASTM A276 316", fontSize: 6, align: "center", wrap: true });
    drawCell({ x: ML + mColW*3, y, w: mColW, h: 10, text: val("seat") || "RPTFE", fontSize: 6, align: "center", wrap: true });
    drawCell({ x: ML + mColW*4, y, w: mColW, h: 10, text: val("seal") || "GRAPHITE", fontSize: 6, align: "center", wrap: true });
    drawCell({ x: ML + mColW*5, y, w: mColW, h: 10, text: val("fasteners") || "STUD: A193 Gr.B8M\nNUT: A194 Gr.8M", fontSize: 5.5, align: "center", wrap: true });
    y += 12;

    // ── PRESURE TESTING ───────────────────────────────────────────────────────
    drawCell({ x: ML, y, w: TW, h: 7, text: "PRESURE TESTING", bold: true, fontSize: 9, align: "center", fill: null });
    y += 7;

    drawCell({ x: ML, y, w: TW, h: 6, text: "VALVE TESTING STANDARD: " + (val("valve_testing_std") || "API 598"), bold: true, fontSize: 8 });
    y += 6;

    // Testing table header
    const tColW = TW / 4;
    drawCell({ x: ML, y, w: tColW, h: 7, text: "TYPE OF TEST", bold: true, fontSize: 7, align: "center", fill: null });
    drawCell({ x: ML + tColW, y, w: tColW, h: 7, text: "MIN.TEST\nPRESSURE", bold: true, fontSize: 6, align: "center", wrap: true, fill: null });
    drawCell({ x: ML + tColW*2, y, w: tColW, h: 7, text: "MIN TEST\nDURATION", bold: true, fontSize: 6, align: "center", wrap: true, fill: null });
    drawCell({ x: ML + tColW*3, y, w: tColW, h: 7, text: "TEST RESULTS", bold: true, fontSize: 7, align: "center", fill: null });
    y += 7;

    // Test rows
    const testData = [
      { type: "HYDRO SHELL", pressure: val("hydro_shell_pressure") || "30", duration: val("hydro_shell_duration") || "2", result: "NO VISIBLE LEAKAGE OBSERVED DURING" },
      { type: "HYDRO SEAT", pressure: val("hydro_seat_pressure") || "22", duration: val("hydro_seat_duration") || "2", result: "TEST PERIOD" },
      { type: "PNEU. SEAT", pressure: val("pneu_seat_pressure") || "6.2", duration: val("pneu_seat_duration") || "2", result: "SEAT LEAKAGE RATE: NO LEAKAGE" },
    ];

    testData.forEach(({ type, pressure, duration, result }, i) => {
      drawCell({ x: ML, y, w: tColW, h: 7, text: type, bold: true, fontSize: 7 });
      drawCell({ x: ML + tColW, y, w: tColW, h: 7, text: pressure, fontSize: 7, align: "center" });
      drawCell({ x: ML + tColW*2, y, w: tColW, h: 7, text: duration, fontSize: 7, align: "center" });
      if (i === 0) {
        drawCell({ x: ML + tColW*3, y, w: tColW, h: 21, text: result + "\n" + testData[1].result + "\n" + testData[2].result, fontSize: 6.5, align: "center", wrap: true, valign: "middle" });
      }
      y += 7;
    });

    // ── REMARK ────────────────────────────────────────────────────────────────
    y += 2;
    const remarkText = val("remark") || 
      "REMARK:\t1) ANTISTATIC AND BLOWOUT STEM DESIGN.\n" +
      "2) FACE-TO-FACE DIMENSIONS OF VALVES AS PER ASME B16.10\n" +
      "3) FLANGE DIMENSIONS AS PER ASME B16.5\n" +
      "4) DESIGN & MANUFACTURING AS PER API 6D, ASME B16.34\n" +
      "- VALVE PERFORMANCE TEST, FUNCTIONAL TEST, VISUAL, DIMENSIONAL, TAGGING, MARKING,\n" +
      "PAINTING INSPECTION FOUND SATISFACTORY.";
    
    const remarkLines = doc.splitTextToSize(remarkText, TW - 4);
    const remarkH = Math.max(25, remarkLines.length * 3.5 + 5);
    drawCell({ x: ML, y, w: TW, h: remarkH, text: remarkText, fontSize: 7, wrap: true, valign: "top" });
    y += remarkH + 5;

    // ── SIGNATURES ────────────────────────────────────────────────────────────
    const halfW = TW / 2;
    const sigHH = 6;

    drawCell({ x: ML, y, w: halfW, h: sigHH, text: "PREPARED BY", bold: true, fontSize: 7.5, align: "center", fill: null });
    drawCell({ x: ML + halfW, y, w: halfW, h: sigHH, text: "APPROVED BY", bold: true, fontSize: 7.5, align: "center", fill: null });
    y += sigHH;

    const signH = 20;

    // Draw signature boxes
    drawCell({ x: ML, y, w: halfW, h: signH, text: "", fontSize: 7, valign: "top" });
    drawCell({ x: ML + halfW, y, w: halfW, h: signH, text: "", fontSize: 7, valign: "top" });

     // Add signature images if available
     const preparedSigDataUrl = val("prepared_by_signature");
     const approvedSigDataUrl = val("approved_by_signature");

     try {
       if (preparedSigDataUrl) {
         const format = getImageFormatFromDataUrl(preparedSigDataUrl);
         addImageContained(doc, preparedSigDataUrl, format, ML, y, halfW, signH, 1);
       }
     } catch (err) {
       console.error("Error adding prepared_by signature:", err);
     }

     try {
       if (approvedSigDataUrl) {
         const format = getImageFormatFromDataUrl(approvedSigDataUrl);
         addImageContained(doc, approvedSigDataUrl, format, ML + halfW, y, halfW, signH, 1);
       }
     } catch (err) {
       console.error("Error adding approved_by signature:", err);
     }

    y += signH;

    const nLW = 16, nVW = halfW - nLW;
    drawCell({ x: ML, y, w: nLW, h: 8, text: "Name -", bold: true, fontSize: 7 });
    drawCell({ x: ML + nLW, y, w: nVW, h: 8, text: val("prepared_by_name", "prepared_by") || "drop down - master", fontSize: 7 });
    drawCell({ x: ML + halfW, y, w: nLW, h: 8, text: "Name", bold: true, fontSize: 7 });
    drawCell({ x: ML + halfW + nLW, y, w: nVW, h: 8, text: val("approved_by") || "drop down master", fontSize: 7 });

    // ── ADD HEAT ANNEXURE PAGE WITH ATTACHMENTS IF ATTACHED ──────────────────
    if (heatAnnexureData && heatAnnexureData.length > 0) {
      addHeatAnnexurePage(doc, heatAnnexureData, 'ball', formData, attachmentImages, watermarkBase64Cache);
    }

    const fileName = `Compliance_Ball_${val("certificate_no", "tcds_no") || "certificate"}_${new Date().toISOString().split("T")[0]}.pdf`;
    const pdfBytes = doc.output('arraybuffer');
    return { fileName, pdfBytes };
  } catch (err) {
    console.error("Error generating Ball Valve PDF:", err);
    throw err;
  }
}

// ══════════════════════════════════════════════════════════════════════════════
// ACTUATOR PDF - Actuator Test Certificate Format (Image 5)
// ══════════════════════════════════════════════════════════════════════════════════════

async function generateActuatorPdf(tcdsData, formData) {
  try {
    const doc = new jsPDF("portrait", "mm", "a4");
    
    await getWatermarkBase64();
    addWatermarkToPage(doc, watermarkBase64Cache);
    
    const { ML, TW, drawCell, val: valHelper } = createPdfHelpers(doc);
    const val = (...keys) => valHelper(formData, tcdsData, ...keys);
    // First page: content starts below the header/watermark area
    let y = HEADER_HEIGHT;

    // Title with QAC-Dept header
    drawCell({ x: ML + TW - 40, y, w: 40, h: 8, text: "QAC-Dept.", bold: true, fontSize: 10, align: "right", border: false });
    drawCell({ x: ML, y, w: TW - 40, h: 8, text: "ACTUATOR TEST CERTIFICATE", bold: true, fontSize: 12, align: "center", border: false });
    y += 8;
    drawCell({ x: ML, y, w: TW, h: 6, text: "( As per EN10204 3.1 )", fontSize: 9, align: "center", border: false });
    y += 8;

    // Header info
    const LW = TW / 2, rH = 7;
    const lx = ML, rx = ML + LW;
    const LL = 40;

    const leftCell = (lbl, txt) => {
      drawCell({ x: lx, y, w: LL, h: rH, text: lbl, bold: true, fontSize: 8 });
      drawCell({ x: lx + LL, y, w: LW - LL, h: rH, text: txt, fontSize: 8 });
    };
    const rightCell = (lbl, txt) => {
      drawCell({ x: rx, y, w: LL, h: rH, text: lbl, bold: true, fontSize: 8 });
      drawCell({ x: rx + LL, y, w: LW - LL, h: rH, text: txt, fontSize: 8 });
    };

    leftCell("PROJECT", val("project", "project_name") || "NA");
    rightCell("DATE", formatDateDDMMYYYY(val("date")) || formatDateDDMMYYYY(new Date()));
    y += rH;

    leftCell("CUSTOMER", val("customer", "customer_name") || "NA");
    rightCell("TCDS NO", val("tcds_no", "section_number") || "NA");
    y += rH;

    const revRowH = 9;
    drawCell({ x: lx, y, w: LL, h: revRowH, text: "JOB OR. NO. DATE", bold: true, fontSize: 8 });
    drawCell({ x: lx + LL, y, w: LW - LL, h: revRowH, text: val("job_order_no") || "", fontSize: 8 });
    drawCell({ x: rx, y, w: LL, h: revRowH, text: "REV NO & DATE", bold: true, fontSize: 8, wrap: true });
    drawCell({ x: rx + LL, y, w: LW - LL, h: revRowH, text: val("rev_no") || "", fontSize: 8 });
    y += revRowH;

    // Job Order details table
    y += 2;
    drawCell({ x: ML, y, w: 35, h: 8, text: "JOB ORDER NO.", bold: true, fontSize: 7, align: "center", fill: null });
    drawCell({ x: ML + 35, y, w: 60, h: 8, text: "PRODUCT DETAILS", bold: true, fontSize: 7, align: "center", fill: null });
    drawCell({ x: ML + 95, y, w: 50, h: 8, text: "SERIAL / BATCH NO.", bold: true, fontSize: 7, align: "center", fill: null });
    drawCell({ x: ML + 145, y, w: TW - 145, h: 8, text: "QTY", bold: true, fontSize: 7, align: "center", fill: null });
    y += 8;

    drawCell({ x: ML, y, w: 35, h: 10, text: val("job_order_no") || "73335", fontSize: 8, align: "center" });
    drawCell({ x: ML + 35, y, w: 60, h: 10, text: "ACTUATOR MODEL: " + (val("actuator_model") || "IPCA7s"), fontSize: 7, align: "center", wrap: true });
    drawCell({ x: ML + 95, y, w: 50, h: 10, text: val("serial_batch_no") || "RPA 00001 &\nRPA00002", fontSize: 7, align: "center", wrap: true });
    drawCell({ x: ML + 145, y, w: TW - 145, h: 10, text: val("qty", "quantity") + " Nos." || "02 NOS.", fontSize: 8, align: "center" });
    y += 12;

    // ── MATERIAL COMPLIANCE ───────────────────────────────────────────────────
    drawCell({ x: ML, y, w: TW, h: 7, text: "MATERIAL COMPLIANCE", bold: true, fontSize: 9, align: "center", fill: null });
    y += 7;

    const matData = [
      { label: "BODY", value: val("body") || "ASTM B241 6063 T6" },
      { label: "PISTON", value: val("piston") || "LM24" },
      { label: "PINION", value: val("pinion") || "ASTM A25 Gr-4140" },
      { label: "END CAPS", value: val("end_caps") || "ASTM B108 LM24" },
      { label: "PRESSURE RATING", value: val("pressure_rating") || "4 kg/cm2" },
      { label: "FAIL ACTION (S.R)", value: val("fail_action_sr") || "FAIL ACTION CLOCKWISE" },
      { label: "FAIL ACTION (D.A)", value: val("fail_action_da") || "NA" },
    ];

    matData.forEach(({ label, value }) => {
      drawCell({ x: ML, y, w: 50, h: 6, text: label, bold: true, fontSize: 7, fill: null });
      drawCell({ x: ML + 50, y, w: TW - 50, h: 6, text: value, fontSize: 7 });
      y += 6;
    });

    y += 3;

    // ── TEST RESULTS ──────────────────────────────────────────────────────────
    drawCell({ x: ML, y, w: TW, h: 7, text: "TEST RESULTS", bold: true, fontSize: 9, align: "center", fill: null });
    y += 7;

    // Leak Test
    drawCell({ x: ML, y, w: 35, h: 8, text: "LEAK TEST\nPRESSURE", bold: true, fontSize: 7, wrap: true, fill: null });
    drawCell({ x: ML + 35, y, w: 25, h: 8, text: val("leak_test_pressure") || "8 kg/cm2", fontSize: 7, align: "center" });
    drawCell({ x: ML + 60, y, w: 25, h: 8, text: "REMARK", bold: true, fontSize: 7, align: "center", fill: null });
    drawCell({ x: ML + 85, y, w: TW - 85, h: 8, text: val("leak_test_remark") || "NO ANY VISIBLE LEAKAGE OR NO ANY PRESSURE DROP OBSERVED", fontSize: 6.5, wrap: true });
    y += 8;

    // Functional Test
    drawCell({ x: ML, y, w: 35, h: 10, text: "FUNCTIONAL TEST", bold: true, fontSize: 7, fill: null });
    drawCell({ x: ML + 35, y, w: TW - 35, h: 10, text: val("functional_test") || "FUNCTIONAL TEST CARRIEDOUT FOR ALL ACTUATORS FOR OPEN / CLOSE OPERATION AND FOUND SATISFACTORY.", fontSize: 6.5, wrap: true });
    y += 10;

    // Visual Inspection
    drawCell({ x: ML, y, w: 35, h: 8, text: "VISUAL INSPECTION", bold: true, fontSize: 7, fill: null });
    drawCell({ x: ML + 35, y, w: TW - 35, h: 8, text: val("visual_inspection") || "ALL ACTUATORS ARE VISUALLY CHECKED AND WORKMANSHIP FOUND TO BE SATISFACTORY / FREE FROM DEFECTS.", fontSize: 6.5, wrap: true });
    y += 10;

    // Declaration
    drawCell({ x: ML, y, w: 35, h: 20, text: "DECLARATION", bold: true, fontSize: 7, fill: null });
    drawCell({ x: ML + 35, y, w: TW - 35, h: 20, text: val("declaration") || 
      "THIS IS TO CERTIFIED THAT, THE ABOVE ACTUATORS ARE MANUFACTURED AND TESTED AT THE PRESSURE INDICATED. THEY CONFORMS TO THE MANUFACTURERS DESIGN SPECIFICATIONS AND ARE FOUND SATISFACTORY IN ALL RESPECT. IT IS CONFORMING TO THE P.O SPECIFICATIONS.", fontSize: 6.5, wrap: true, valign: "top" });
    y += 22;

    // ── SIGNATURE ─────────────────────────────────────────────────────────────
    drawCell({ x: ML, y, w: TW, h: 6, text: "PREPARED BY", bold: true, fontSize: 8 });
    y += 6;

    const signHActuator = 15;
    const preparedSigDataUrl = val("prepared_by_signature");

    drawCell({ x: ML, y, w: TW, h: signHActuator, text: "", fontSize: 7, valign: "top" });

    try {
      if (preparedSigDataUrl) {
        const format = getImageFormatFromDataUrl(preparedSigDataUrl);
         addImageContained(doc, preparedSigDataUrl, format, ML, y, TW, signHActuator, 1);
      }
    } catch (err) {
      console.error("Error adding prepared_by signature:", err);
    }

    y += signHActuator + 2;

    drawCell({ x: ML, y, w: TW, h: 6, text: "QAC OFFICER", bold: true, fontSize: 8 });
    y += 6;

    const qacSigDataUrl = val("qac_officer_signature");

    drawCell({ x: ML, y, w: TW, h: signHActuator, text: "", fontSize: 7, valign: "top" });

    try {
      if (qacSigDataUrl) {
        const format = getImageFormatFromDataUrl(qacSigDataUrl);
         addImageContained(doc, qacSigDataUrl, format, ML, y, TW, signHActuator, 1);
      }
    } catch (err) {
      console.error("Error adding qac_officer signature:", err);
    }

    y += signHActuator + 2;

    // Format footer
    drawCell({ x: ML, y, w: TW, h: 5, text: "F/QAC/33 REV-01 DT-10/06/2024", fontSize: 6, border: false });

    const fileName = `Actuator_TC_${val("certificate_no", "tcds_no") || "certificate"}_${new Date().toISOString().split("T")[0]}.pdf`;
    const pdfBytes = doc.output('arraybuffer');
    return { fileName, pdfBytes };
  } catch (err) {
    console.error("Error generating Actuator PDF:", err);
    throw err;
  }
}

// ══════════════════════════════════════════════════════════════════════════════
// LSB PDF - LSB Test Certificate Format (Image 6)
// ══════════════════════════════════════════════════════════════════════════════

async function generateLSBPdf(tcdsData, formData) {
  try {
    const doc = new jsPDF("portrait", "mm", "a4");
    
    await getWatermarkBase64();
    addWatermarkToPage(doc, watermarkBase64Cache);
    
    const { ML, TW, drawCell, val: valHelper } = createPdfHelpers(doc);
    const val = (...keys) => valHelper(formData, tcdsData, ...keys);
    // First page: content starts below the header/watermark area
    let y = HEADER_HEIGHT;

    // Title
    drawCell({ x: ML, y, w: TW, h: 10, text: "LSB TEST CERTIFICATE", bold: true, fontSize: 14, align: "center", border: false });
    y += 10;
    drawCell({ x: ML, y, w: TW, h: 6, text: "ACCORDING TO EN 10204 TYPE 3.1", fontSize: 9, align: "center", border: false });
    y += 8;

    // Header info
    const LW = TW / 2, rH = 7;
    const lx = ML, rx = ML + LW;
    const LL = 45;

    const leftCell = (lbl, txt) => {
      drawCell({ x: lx, y, w: LL, h: rH, text: lbl, bold: true, fontSize: 8 });
      drawCell({ x: lx + LL, y, w: LW - LL, h: rH, text: ": " + txt, fontSize: 8 });
    };
    const rightCell = (lbl, txt) => {
      drawCell({ x: rx, y, w: LL - 10, h: rH, text: lbl, bold: true, fontSize: 8 });
      drawCell({ x: rx + LL - 10, y, w: LW - LL + 10, h: rH, text: ": " + txt, fontSize: 8 });
    };

    leftCell("CERTIFICATE NO.", val("certificate_no") || "IPCIPL/CC/LSB/01");
    rightCell("DATE", formatDateDDMMYYYY(val("date")) || formatDateDDMMYYYY(new Date()));
    y += rH;

    leftCell("CUSTOMER", val("customer", "customer_name") || "M/S NA");
    drawCell({ x: rx, y, w: LW, h: rH, text: "", fontSize: 8 });
    y += rH;

    leftCell("P.O. & J.O. NO. & DATE", val("po_no", "purchase_order_no") || "NA");
    rightCell("Date", formatDateDDMMYYYY(val("po_date")) || "NA");
    y += rH;

    leftCell("TCDS NO. & DATE", val("tcds_no", "section_number") || "NA");
    rightCell("Date", formatDateDDMMYYYY(val("tcds_date")) || "NA");
    y += rH;

    leftCell("QTY.", val("qty", "quantity") + " NOS." || "02 NOS.");
    drawCell({ x: rx, y, w: LW, h: rH, text: "", fontSize: 8 });
    y += rH;

    leftCell("SERIAL NO.", val("serial_no", "sr_no") || "LSB00001, LSB00002");
    drawCell({ x: rx, y, w: LW, h: rH, text: "", fontSize: 8 });
    y += rH + 3;

    // ── LSB DETAILS ───────────────────────────────────────────────────────────
    // Section header spanning full width
    drawCell({ x: ML, y, w: TW, h: 8, text: "LSB DETAILS :", bold: true, fontSize: 9, fill: null });
    y += 8;

    const detailRows = [
      { label: "MAKE:", value: val("make") || "IPC" },
      { label: "ENCLOSURE :", value: val("enclosure") || "ALUMINIUM" },
      { label: "MODEL:", value: val("model") || "FPB-F-1-2-H-1-4-01-0" },
      { label: "SWITCH / SENSOR TYPE:", value: val("switch_type") || "MICROSWITCH" },
      { label: "SWITCH / SENSOR MODEL:", value: val("switch_model") || "V15505-CZ100A05-01" },
      { label: "SWITCH / SENSOR QTY :", value: (val("switch_qty") ? val("switch_qty") + " Nos" : "02 Nos") },
      { label: "SWITCH / SENSOR MAKE:", value: val("switch_make") || "HONEYWELL" },
      { label: "VOLTAGE :", value: val("voltage") || "5A-125/250 VAC; 0.1A-48 VDC" },
      { label: "CABLE ENTRY & QTY :", value: val("cable_entry") || "1/2\"NPT(F) - 02 NOS" },
      { label: "PROTECTION :", value: val("protection") || "FLAME PROOF" },
      { label: "CERTIFICATION :", value: val("certification") || "PESO CERTIFIED A/P/HQ/MH/104/7536\n(P554146)" },
    ];

    const labelColW = 65;
    const valueColW = TW - labelColW;
    detailRows.forEach(({ label, value }) => {
      const isCert = label.includes("CERTIFICATION");
      const rowH = isCert ? 12 : 7;
      drawCell({ x: ML, y, w: labelColW, h: rowH, text: label, bold: true, fontSize: 7 });
      drawCell({ x: ML + labelColW, y, w: valueColW, h: rowH, text: value, fontSize: 7, wrap: isCert });
      y += rowH;
    });

    y += 5;

    // ── REMARK ────────────────────────────────────────────────────────────────
    const remarkText = val("remark") || 
      "REMARK : THE LSB ARE INSPECTED & TESTED AS PER RELEVANT STANDARDS, TECHNICAL DOCUMENTATION, SCHEDULE DRAWING AND FOUND SATISFACTORY.";
    drawCell({ x: ML, y, w: TW, h: 12, text: remarkText, fontSize: 7, wrap: true, valign: "top" });
    y += 15;

    // ── SIGNATURES (2 boxes only: PREPARED BY + APPROVED BY) ─────────────────
    const halfWSig = TW / 2;

    drawCell({ x: ML, y, w: halfWSig, h: 6, text: "PREPARED BY", bold: true, fontSize: 8, align: "center", fill: null });
    drawCell({ x: ML + halfWSig, y, w: halfWSig, h: 6, text: "APPROVED BY", bold: true, fontSize: 8, align: "center", fill: null });
    y += 6;

    const preparedSigDataUrl = val("prepared_by_signature");
    const approvedSigDataUrl = val("approved_by_signature");

    const signHLSB = 20;
    drawCell({ x: ML, y, w: halfWSig, h: signHLSB, text: "", fontSize: 7, valign: "top" });
    drawCell({ x: ML + halfWSig, y, w: halfWSig, h: signHLSB, text: "", fontSize: 7, valign: "top" });

    if (preparedSigDataUrl) {
      try {
        const format = getImageFormatFromDataUrl(preparedSigDataUrl);
        addImageContained(doc, preparedSigDataUrl, format, ML, y, halfWSig, signHLSB, 1);
      } catch (err) {
        console.error("Error adding prepared_by signature (LSB):", err);
      }
    }

    if (approvedSigDataUrl) {
      try {
        const format = getImageFormatFromDataUrl(approvedSigDataUrl);
        addImageContained(doc, approvedSigDataUrl, format, ML + halfWSig, y, halfWSig, signHLSB, 1);
      } catch (err) {
        console.error("Error adding approved_by signature (LSB):", err);
      }
    }

    y += signHLSB;

    drawCell({ x: ML, y, w: halfWSig, h: 8, text: val("prepared_by_name", "prepared_by") || "", bold: true, fontSize: 8, align: "center", border: false });
    drawCell({ x: ML + halfWSig, y, w: halfWSig, h: 8, text: val("approved_by") || "", bold: true, fontSize: 8, align: "center", border: false });

    // ── FOOTER ON ALL PAGES ───────────────────────────────────────────────────
    const pageH = doc.internal.pageSize.getHeight();
    const totalPages = doc.internal.pages.length - 1;
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      doc.setFontSize(7);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(0, 0, 0);
      doc.text("Format No. F/QAC/Ex-01 REV:00 DT: 18.03.2020", ML, pageH - 5);
      doc.text(`Page ${i} of ${totalPages}`, ML + TW, pageH - 5, { align: "right" });
    }
    const fileName = `LSB_TC_${val("certificate_no", "tcds_no") || "certificate"}_${new Date().toISOString().split("T")[0]}.pdf`;
    const pdfBytes = doc.output('arraybuffer');
    return { fileName, pdfBytes };
  } catch (err) {
    console.error("Error generating LSB PDF:", err);
    throw err;
  }
}

// ══════════════════════════════════════════════════════════════════════════════
// AUTOMATION CERTIFICATE PDF - Certificate of Compliance Format
// ══════════════════════════════════════════════════════════════════════════════════════

async function generateAutomationPdf(tcdsData, formData, heatAnnexureData = null, attachmentImages = null) {
  try {
    const doc = new jsPDF("portrait", "mm", "a4");
    
    await getWatermarkBase64();
    addWatermarkToPage(doc, watermarkBase64Cache);
    
    const { ML, TW, drawCell, val: valHelper } = createPdfHelpers(doc);
    const val = (...keys) => valHelper(formData, tcdsData, ...keys);
    // First page: content starts below the header/watermark area
    let y = HEADER_HEIGHT;

    // Title - clean format with two lines and bottom border
    drawCell({ x: ML, y, w: TW, h: 8, text: "Certificate of Compliance", bold: true, fontSize: 11, align: "center", border: false });
    y += 8;
    drawCell({ x: ML, y, w: TW, h: 6, text: "According to EN 10204 Type 3.1", bold: true, fontSize: 9, align: "center", border: false });
    y += 6;
    // Draw horizontal line under title
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.3);
    doc.line(ML, y, ML + TW, y);
    y += 4;

    // Certificate Details - 4 column layout
    const col = TW / 4;
    const rowH = 5;

    // Row 1
    drawCell({ x: ML, y, w: col, h: rowH, text: "CERTIFICATE NO.", bold: true, fontSize: 6.5, fill: null });
    drawCell({ x: ML + col, y, w: col, h: rowH, text: val("certificate_no") || "", fontSize: 6.5 });
    drawCell({ x: ML + col * 2, y, w: col, h: rowH, text: "CERTI. DATE", bold: true, fontSize: 6.5, fill: null });
    drawCell({ x: ML + col * 3, y, w: col, h: rowH, text: formatDateDDMMYYYY(val("date")) || "", fontSize: 6.5 });
    y += rowH;

    // Row 2
    drawCell({ x: ML, y, w: col, h: rowH, text: "CUSTOMER NAME", bold: true, fontSize: 6.5, fill: null });
    drawCell({ x: ML + col, y, w: col, h: rowH, text: val("customer") || "", fontSize: 6.5 });
    drawCell({ x: ML + col * 2, y, w: col, h: rowH, text: "P. O. DATE", bold: true, fontSize: 6.5, fill: null });
    drawCell({ x: ML + col * 3, y, w: col, h: rowH, text: formatDateDDMMYYYY(val("po_date")) || "", fontSize: 6.5 });
    y += rowH;

    // Row 3
    drawCell({ x: ML, y, w: col, h: rowH, text: "PROJECT NAME", bold: true, fontSize: 6.5, fill: null });
    drawCell({ x: ML + col, y, w: col, h: rowH, text: val("project_name") || "", fontSize: 6.5 });
    drawCell({ x: ML + col * 2, y, w: col, h: rowH, text: "O. A. DATE", bold: true, fontSize: 6.5, fill: null });
    drawCell({ x: ML + col * 3, y, w: col, h: rowH, text: formatDateDDMMYYYY(val("oa_date")) || "", fontSize: 6.5 });
    y += rowH;

    // Row 4
    drawCell({ x: ML, y, w: col, h: rowH, text: "CUSTOMER P.O.NO.", bold: true, fontSize: 6.5, fill: null });
    drawCell({ x: ML + col, y, w: col, h: rowH, text: val("po_no") || "", fontSize: 6.5 });
    drawCell({ x: ML + col * 2, y, w: col, h: rowH, text: "O.A. DRAWING NO.", bold: true, fontSize: 6.5, fill: null });
    drawCell({ x: ML + col * 3, y, w: col, h: rowH, text: val("ga_drawing_no") || "", fontSize: 6.5 });
    y += rowH;

    // Row 5
    drawCell({ x: ML, y, w: col, h: rowH, text: "Q. A. NO.", bold: true, fontSize: 6.5, fill: null });
    drawCell({ x: ML + col, y, w: col, h: rowH, text: val("oa_no") || "", fontSize: 6.5 });
    drawCell({ x: ML + col * 2, y, w: col, h: rowH, text: "QAP NO.", bold: true, fontSize: 6.5, fill: null });
    drawCell({ x: ML + col * 3, y, w: col, h: rowH, text: val("qap_no") || "", fontSize: 6.5 });
    y += rowH;

    // Row 6
    drawCell({ x: ML, y, w: col, h: rowH, text: "QA DRAWING NO.", bold: true, fontSize: 6.5, fill: null });
    drawCell({ x: ML + col, y, w: col, h: rowH, text: tcdsData?.qa_drawing_no || tcdsData?.qad_no || "", fontSize: 6.5 });
    drawCell({ x: ML + col * 2, y, w: col, h: rowH, text: "SIZE & PRE. RATING", bold: true, fontSize: 6.5, fill: null });
    const sizeClass = val("size") && val("class") ? `${val("size")} (${val("class")})` : "";
    drawCell({ x: ML + col * 3, y, w: col, h: rowH, text: sizeClass, fontSize: 6.5 });
    y += rowH;

    // Row 8
    drawCell({ x: ML, y, w: col, h: rowH, text: "FAIL POSITION", bold: true, fontSize: 6.5, fill: null });
    drawCell({ x: ML + col, y, w: col, h: rowH, text: val("fail_position") || "", fontSize: 6.5 });
    drawCell({ x: ML + col * 2, y, w: col, h: rowH, text: "POWER FAIL POSITION", bold: true, fontSize: 6.5, fill: null });
    drawCell({ x: ML + col * 3, y, w: col, h: rowH, text: val("power_fail_position") || "", fontSize: 6.5 });
    y += rowH;

    // Row 9
    drawCell({ x: ML, y, w: col, h: rowH, text: "QTY. (NOS.)", bold: true, fontSize: 6.5, fill: null });
    drawCell({ x: ML + col, y, w: col, h: rowH, text: val("qty") || "", fontSize: 6.5 });
    drawCell({ x: ML + col * 2, y, w: col * 2, h: rowH, text: "", fontSize: 6.5 });
    y += rowH;

    // Row 10
    drawCell({ x: ML, y, w: col, h: rowH, text: "CUSTOMER ITEM CODE", bold: true, fontSize: 6.5, fill: null });
    drawCell({ x: ML + col, y, w: col * 3, h: rowH, text: val("customer_item_code") || "", fontSize: 6.5 });
    y += rowH + 2;

    // Check if valve exists in TCDS OR if there are accessories
    const valveHasData = tcdsData?.valve_type || tcdsData?.make || tcdsData?.series || tcdsData?.type;
    const hasAccessories = tcdsData?.accessories_data &&
      (Array.isArray(tcdsData.accessories_data) ? tcdsData.accessories_data.length > 0 : true);
    const showAssembly = valveHasData || hasAccessories;

    // Show assembly section if valve exists OR accessories exist
    if (showAssembly) {
      // IPC Sr. No. / Valve Batch Sr. No. / Tag No. Table (only if valve has data)
      if (valveHasData) {
        const col2 = TW / 6;
        drawCell({ x: ML, y, w: col2, h: 5, text: "IPC SR. NO.", bold: true, fontSize: 6.5, align: "center", fill: null });
        drawCell({ x: ML + col2, y, w: col2, h: 5, text: "VALVE BATCH/SR.NO.", bold: true, fontSize: 6.5, align: "center", fill: null });
        drawCell({ x: ML + col2 * 2, y, w: col2, h: 5, text: "TAG NO.", bold: true, fontSize: 6.5, align: "center", fill: null });
        drawCell({ x: ML + col2 * 3, y, w: col2, h: 5, text: "IPC SR. NO.", bold: true, fontSize: 6.5, align: "center", fill: null });
        drawCell({ x: ML + col2 * 4, y, w: col2, h: 5, text: "VALVE BATCH/SR.NO.", bold: true, fontSize: 6.5, align: "center", fill: null });
        drawCell({ x: ML + col2 * 5, y, w: col2, h: 5, text: "TAG NO.", bold: true, fontSize: 6.5, align: "center", fill: null });
        y += 5;

        // Parse arrays - handle both JSON array strings and plain strings
const parseArray = (val) => {
  if (!val) return [];
  if (Array.isArray(val)) return val;
  try { return JSON.parse(val); } catch { return [val]; }
};

const ipcArr = parseArray(formData.ipc_sr_no_array?.length ? formData.ipc_sr_no_array : formData.ipc_sr_no);
const batchArr = parseArray(formData.valve_batch_sr_no_array?.length ? formData.valve_batch_sr_no_array : formData.valve_batch_sr_no);
const tagArr = parseArray(formData.tag_numbers_array?.length ? formData.tag_numbers_array : formData.tag_no);

const qty = Math.max(ipcArr.length, batchArr.length, tagArr.length, 1);
const rowsPerPage = 6; // 6 entries per row (3 left, 3 right)

for (let i = 0; i < qty; i += rowsPerPage) {
  const batch = Array.from({ length: rowsPerPage }, (_, j) => i + j);
  
  // Left 3 columns
  drawCell({ x: ML, y, w: col2, h: 5, text: ipcArr[batch[0]] || "", fontSize: 6.5, align: "center" });
  drawCell({ x: ML + col2, y, w: col2, h: 5, text: batchArr[batch[0]] || "", fontSize: 6.5, align: "center" });
  drawCell({ x: ML + col2 * 2, y, w: col2, h: 5, text: tagArr[batch[0]] || "", fontSize: 6.5, align: "center" });
  // Right 3 columns (next entry)
  drawCell({ x: ML + col2 * 3, y, w: col2, h: 5, text: ipcArr[batch[3]] || "", fontSize: 6.5, align: "center" });
  drawCell({ x: ML + col2 * 4, y, w: col2, h: 5, text: batchArr[batch[3]] || "", fontSize: 6.5, align: "center" });
  drawCell({ x: ML + col2 * 5, y, w: col2, h: 5, text: tagArr[batch[3]] || "", fontSize: 6.5, align: "center" });
  y += 5;

  // Rows 2 and 3 of this batch
  for (let r = 1; r <= 2; r++) {
    drawCell({ x: ML, y, w: col2, h: 5, text: ipcArr[batch[r]] || "", fontSize: 6.5, align: "center" });
    drawCell({ x: ML + col2, y, w: col2, h: 5, text: batchArr[batch[r]] || "", fontSize: 6.5, align: "center" });
    drawCell({ x: ML + col2 * 2, y, w: col2, h: 5, text: tagArr[batch[r]] || "", fontSize: 6.5, align: "center" });
    drawCell({ x: ML + col2 * 3, y, w: col2, h: 5, text: ipcArr[batch[r + 3]] || "", fontSize: 6.5, align: "center" });
    drawCell({ x: ML + col2 * 4, y, w: col2, h: 5, text: batchArr[batch[r + 3]] || "", fontSize: 6.5, align: "center" });
    drawCell({ x: ML + col2 * 5, y, w: col2, h: 5, text: tagArr[batch[r + 3]] || "", fontSize: 6.5, align: "center" });
    y += 5;
  }
}
y += 2;
      }

      // Testing Pressure Details
      if (valveHasData) {
        drawCell({ x: ML, y, w: TW, h: 5, text: "TESTING PRESSURE DETAILS", bold: true, fontSize: 8, align: "center", fill: null });
        y += 5;

        const col3 = TW / 4;
        drawCell({ x: ML, y, w: col3, h: 5, text: "TESTING STANDARD", bold: true, fontSize: 6.5, align: "center", fill: null });
        drawCell({ x: ML + col3, y, w: col3, h: 5, text: val("testing_standard") || "", fontSize: 6.5, align: "center" });
        drawCell({ x: ML + col3 * 2, y, w: col3, h: 5, text: "MIN AIR SUPPLY TO ACTUATOR", bold: true, fontSize: 6.5, align: "center", fill: null });
        drawCell({ x: ML + col3 * 3, y, w: col3, h: 5, text: val("min_air_supply") || "NA", fontSize: 6.5, align: "center" });
        y += 5;

        drawCell({ x: ML, y, w: col3, h: 5, text: "PRESSURE RATING", bold: true, fontSize: 6.5, align: "center", fill: null });
        drawCell({ x: ML + col3, y, w: col3, h: 5, text: val("pressure_rating") || "", fontSize: 6.5, align: "center" });
        drawCell({ x: ML + col3 * 2, y, w: col3, h: 5, text: "BODY HYDROSTATIC", bold: true, fontSize: 6.5, align: "center", fill: null });
        drawCell({ x: ML + col3 * 3, y, w: col3, h: 5, text: val("body_hydrostatic") || "", fontSize: 6.5, align: "center" });
        y += 5;

        drawCell({ x: ML, y, w: col3, h: 5, text: "SEAT HYDROSTATIC", bold: true, fontSize: 6.5, align: "center", fill: null });
        drawCell({ x: ML + col3, y, w: col3, h: 5, text: val("seat_hydrostatic") || "", fontSize: 6.5, align: "center" });
        drawCell({ x: ML + col3 * 2, y, w: col3, h: 5, text: "SEAT PNEUMATIC", bold: true, fontSize: 6.5, align: "center", fill: null });
        drawCell({ x: ML + col3 * 3, y, w: col3, h: 5, text: val("seat_pneumatic") || "", fontSize: 6.5, align: "center" });
        y += 5;

        drawCell({ x: ML, y, w: TW / 2, h: 5, text: "TEST DURATION", bold: true, fontSize: 6.5, align: "center", fill: null });
        drawCell({ x: ML + TW / 2, y, w: TW / 2, h: 5, text: val("test_duration") || "", fontSize: 6.5, align: "center" });
        y += 5;

        drawCell({ x: ML, y, w: TW, h: 5, text: "OBSERVATION", bold: true, fontSize: 6.5, align: "center", fill: null });
        y += 5;
        drawCell({ x: ML, y, w: TW, h: 6, text: val("observation") || "", fontSize: 6.5, wrap: true });
        y += 8;
      }

      // ASSEMBLY CONSISTS OF header
      drawCell({ x: ML, y, w: TW, h: 5, text: "ASSEMBLY CONSISTS OF:", bold: true, fontSize: 8, fill: null });
      y += 5;

      // DETAILS OF VALVE (only if valve has data)
      if (valveHasData) {
        drawCell({ x: ML, y, w: TW, h: 5, text: "DETAILS OF VALVE:", bold: true, fontSize: 8, fill: null });
        y += 5;

        // Extract valve details using same logic as form (getFormattedValveDetails)
        const vatValues = {
          'make': 'MAKE',
          'series': 'SERIES',
          'type': 'TYPE',
          'end_connection': 'END CONNECTION',
          'size': 'SIZE',
          'class': 'CLASS',
          'body': 'BODY',
          'disc': 'DISC',
          'seat': 'SEAT',
          'spring': 'SPRING',
          'operator': 'OPERATOR'
        };

        const valveDetails = [];
        Object.keys(vatValues).forEach(key => {
          const label = vatValues[key];
          const value = tcdsData?.[key] || tcdsData?.[label] || '';
          if (value) {
            valveDetails.push(`${label}: ${value}`);
          }
        });

        const valveDetailsText = valveDetails.length > 0 ? valveDetails.join(', ') : 'NR';
        drawCell({ x: ML, y, w: TW, h: 15, text: valveDetailsText, fontSize: 6, wrap: true });
        y += 15;
      }
    }

    // Page break check - reserve 20mm at bottom for footer
    if (y > 260) {
      doc.addPage();
      addWatermarkToPage(doc, watermarkBase64Cache);
      // FIX: Reset y to below header area on continuation pages
      y = HEADER_HEIGHT;
    }

    // Helper function to format accessory details with ALL parameters from database
// Helper function to deeply flatten nested objects into KEY: VALUE pairs
    const deepFlattenObject = (obj) => {
      const result = [];
      const excludeKeys = ['id', 'tcds_id', 'type', 'accessoryName', 'component', 'created_at', 'updated_at', 'createdAt', 'updatedAt', 'accessoryId'];

      const processValue = (value, keyLabel) => {
        if (value === null || value === undefined || value === '') return;

        // Parse JSON strings
        if (typeof value === 'string') {
          const trimmed = value.trim();
          if (!trimmed) return;

          try {
            const parsed = JSON.parse(trimmed);
            if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
              // Recursively process parsed object
              Object.keys(parsed).forEach(k => {
                const v = parsed[k];
                if (v && !excludeKeys.includes(k)) {
                  processValue(v, k);
                }
              });
              return;
            }
          } catch (e) {
            // Not JSON, treat as string
          }

          // Add string value
          if (trimmed && trimmed.toLowerCase() !== 'null' && trimmed.toLowerCase() !== 'undefined') {
            result.push(`${keyLabel}: ${trimmed}`);
          }
        } else if (typeof value === 'object' && !Array.isArray(value)) {
          // Recursively flatten nested objects
          Object.keys(value).forEach(k => {
            const v = value[k];
            if (v && !excludeKeys.includes(k)) {
              processValue(v, k);
            }
          });
        } else if (Array.isArray(value)) {
          // Handle arrays
          const arrayItems = value
            .filter(item => item !== null && item !== undefined && item !== '')
            .map(item => typeof item === 'object' ? JSON.stringify(item) : String(item))
            .join(', ');
          if (arrayItems) {
            result.push(`${keyLabel}: ${arrayItems}`);
          }
        } else {
          // Handle primitives
          const valueStr = String(value).trim();
          if (valueStr && valueStr.toLowerCase() !== 'null' && valueStr.toLowerCase() !== 'undefined') {
            result.push(`${keyLabel}: ${valueStr}`);
          }
        }
      };

      // Start processing
      Object.keys(obj).forEach(key => {
        if (excludeKeys.includes(key)) return;
        const value = obj[key];
        const keyLabel = key.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').toUpperCase().trim();
        processValue(value, keyLabel);
      });

      return result;
    };

    // Helper function to format accessory details with ALL parameters from database
    const formatAccessoryDetails = (accessoryObj) => {
      if (typeof accessoryObj === 'string') {
        try {
          accessoryObj = JSON.parse(accessoryObj);
        } catch {
          return accessoryObj;
        }
      }
      if (!accessoryObj) return "NR";

      const params = deepFlattenObject(accessoryObj);
      if (params.length === 0) {
        const fallback = accessoryObj.description || accessoryObj.name || accessoryObj.accessoryName || "NR";
        return fallback;
      }

      return params.join(' , ');
    };

    // Extract accessory details map (same logic as form's getAccessoryDetails)
// Extract accessory details map using deepFlattenObject
// Extract accessory details map
    const getAccessoriesMap = () => {
      if (!tcdsData || !tcdsData.accessories_data) return {};

      const accessoriesData = typeof tcdsData.accessories_data === 'string'
        ? JSON.parse(tcdsData.accessories_data)
        : tcdsData.accessories_data;

      const accessoryDetailsMap = {};
      const excludeKeys = ['id', 'tcds_id', 'type', 'accessoryName', 'component', 
                           'created_at', 'updated_at', 'createdAt', 'updatedAt', 
                           'accessoryId', 'input_type', 'dropdown_values'];

      if (Array.isArray(accessoriesData)) {
        accessoriesData.forEach(acc => {
          const accType = (acc.accessoryName || acc.component || acc.type || '').toLowerCase().trim();
          if (accType && !accessoryDetailsMap[accType]) {
            const details = [];

            Object.keys(acc).forEach(key => {
              if (excludeKeys.includes(key)) return;

              const value = acc[key];
              if (!value || value === '') return;

              // parameters is an array of {name, value, input_type, dropdown_values}
              // extract only name: value pairs from it
              if (key === 'parameters' && Array.isArray(value)) {
                value.forEach(param => {
                  if (param && param.name && param.value !== undefined && param.value !== null && param.value !== '') {
                    details.push(`${param.name}: ${param.value}`);
                  }
                });
                return;
              }

              // Handle parameters as JSON string
              if (key === 'parameters' && typeof value === 'string') {
                try {
                  const parsed = JSON.parse(value);
                  if (Array.isArray(parsed)) {
                    parsed.forEach(param => {
                      if (param && param.name && param.value !== undefined && param.value !== null && param.value !== '') {
                        details.push(`${param.name}: ${param.value}`);
                      }
                    });
                  }
                } catch (e) { /* ignore */ }
                return;
              }

              // Regular string values
              if (typeof value === 'string') {
                details.push(`${key.toUpperCase().replace(/_/g, ' ')}: ${value}`);
              }
            });

            if (details.length > 0) {
              accessoryDetailsMap[accType] = details.join(' , ');
            }
          }
        });
      }

      return accessoryDetailsMap;
    };
    
    
    const accessoriesMap = getAccessoriesMap();

    // ACTUATOR details
    // Dynamically render all accessories
// Dynamically render all accessories with calculated heights
const accessoryLabels = {
  'actuator': 'DETAILS OF ACTUATOR',
  'positioner': 'DETAILS OF POSITIONER',
  'air filter regulator': 'DETAILS OF AIR FILTER REGULATOR',
  'solenoid valve': 'DETAILS OF SOLENOID VALVE',
  'limit switch box': 'DETAILS OF LIMIT SWITCH BOX',
  'airlock relay': 'DETAILS OF AIRLOCK RELAY',
  'pilot valve': 'DETAILS OF PILOT VALVE',
  'volume booster': 'DETAILS OF VOLUME BOOSTER',
  'manual override': 'DETAILS OF MANUAL OVERRIDE'
};

Object.keys(accessoriesMap).forEach(accType => {
  const accData = accessoriesMap[accType];
  if (!accData) return;

  const label = accessoryLabels[accType] || `DETAILS OF ${accType.toUpperCase()}`;

  // Calculate required height based on text wrapping
  const lines = doc.splitTextToSize(accData, TW - 4);
  const lineHeight = 3.5;
  const padding = 2;
  const cellHeight = Math.max(10, (lines.length * lineHeight) + padding);

  // Page break check before adding accessory
  const estimatedHeight = 5 + cellHeight;
  if (y + estimatedHeight > 260) {
    doc.addPage();
    addWatermarkToPage(doc, watermarkBase64Cache);
    y = HEADER_HEIGHT;
  }

  drawCell({ x: ML, y, w: TW, h: 5, text: label + ":", bold: true, fontSize: 8, fill: null });
  y += 5;
  drawCell({ x: ML, y, w: TW, h: cellHeight, text: accData, fontSize: 6, wrap: true });
  y += cellHeight;
});

    // Page break check before remarks section
    if (y + 25 > 260) {
      doc.addPage();
      addWatermarkToPage(doc, watermarkBase64Cache);
      y = HEADER_HEIGHT;
    }

    drawCell({ x: ML, y, w: TW, h: 5, text: "REMARK:", bold: true, fontSize: 8, fill: null });
    y += 5;
    drawCell({ x: ML, y, w: TW, h: 4, text: val("remark") || "", fontSize: 6, wrap: true });
    y += 8;

    drawCell({ x: ML, y, w: TW, h: 5, text: "DECLARATION OF COMPLIANCE", bold: true, fontSize: 8, fill: null });
    y += 5;
    const declaration = val("declaration_of_compliance") || "We here by complying that the above valve / valves automations are manufactured, inspected and tested in accordance with customer purchase order, technical specifications, drawings.";
    drawCell({ x: ML, y, w: TW, h: 10, text: declaration, fontSize: 6, wrap: true });
    y += 10;

    drawCell({ x: ML, y, w: TW, h: 5, text: "LEGENDS", bold: true, fontSize: 8, fill: null });
    y += 5;
    const legends = "BV - Ball Valve; FB - Full Bore; RB - Reduced Bore; FS - Firesafe; RFS - Raised Face Serrated; RFF - Raised Face Flat; SOV - Solenoid Valve; LMS - Limit Switch Box; AF/R - Air Filter/Regulator; FC - Fail Close; FO - Fail Open; FST - Fail Stayput; LPI - Local Position Indicator; PPOS - Pneumatic Positioner; I/P - I To P Converter; PF - Power Fail; DA - Double Acting";
    drawCell({ x: ML, y, w: TW, h: 10, text: legends, fontSize: 5.5, wrap: true });
    y += 10;

    // Page break check before signatures
    if (y + 60 > 260) {
      doc.addPage();
      addWatermarkToPage(doc, watermarkBase64Cache);
      y = HEADER_HEIGHT;
    }

    // Signatures - proper table format matching reference image
    y += 3;
    const thirdW = TW / 3;

    // Header row
    drawCell({ x: ML, y, w: thirdW, h: 5, text: "Prepared By", bold: true, fontSize: 7, align: "center" });
    drawCell({ x: ML + thirdW, y, w: thirdW, h: 5, text: "For Integral Process Controls India (P) Ltd", bold: true, fontSize: 7, align: "center" });
    drawCell({ x: ML + thirdW * 2, y, w: thirdW, h: 5, text: "Third Party (If Any)", bold: true, fontSize: 7, align: "center" });
    y += 5;

    // Large signature/stamp area
    const signHAuto = 45;
    drawCell({ x: ML, y, w: thirdW, h: signHAuto, text: "", fontSize: 6, align: "center", valign: "middle" });
    drawCell({ x: ML + thirdW, y, w: thirdW, h: signHAuto, text: "", fontSize: 6, align: "center", valign: "middle" });
    drawCell({ x: ML + thirdW * 2, y, w: thirdW, h: signHAuto, text: "", fontSize: 6, align: "center", valign: "middle" });

     const preparedSigDataUrl = val("prepared_by_signature");
     const approvedSigDataUrl = val("approved_by_signature");

     if (preparedSigDataUrl) {
       try {
         const format = getImageFormatFromDataUrl(preparedSigDataUrl);
         addImageContained(doc, preparedSigDataUrl, format, ML, y, thirdW, signHAuto, 1);
       } catch (err) {
         console.error("Error adding prepared_by signature (automation):", err);
       }
     }

     if (approvedSigDataUrl) {
       try {
         const format = getImageFormatFromDataUrl(approvedSigDataUrl);
         addImageContained(doc, approvedSigDataUrl, format, ML + thirdW, y, thirdW, signHAuto, 1);
       } catch (err) {
         console.error("Error adding approved_by signature (automation):", err);
       }
     }

    y += signHAuto;

    // Footer labels row
    drawCell({ x: ML, y, w: thirdW, h: 5, text: "QAC Engineer / Officer", bold: true, fontSize: 6.5, align: "center" });
    drawCell({ x: ML + thirdW, y, w: thirdW, h: 5, text: "Approved By", bold: true, fontSize: 6.5, align: "center" });
    drawCell({ x: ML + thirdW * 2, y, w: thirdW, h: 5, text: "", fontSize: 6.5, align: "center" });

    // ── ADD HEAT ANNEXURE PAGE WITH ATTACHMENTS IF ATTACHED ──────────────────
    if (heatAnnexureData && heatAnnexureData.length > 0) {
      addHeatAnnexurePage(doc, heatAnnexureData, 'automation', formData, attachmentImages, watermarkBase64Cache);
    }

    // ── ADD FOOTERS TO ALL PAGES (Two-Line Layout) ──────────────────
    const addFooterCallback = () => {
      const totalPages = doc.internal.pages.length - 1;
      const pageW = doc.internal.pageSize.getWidth();
      const pageH = doc.internal.pageSize.getHeight();
      const footerStartY = pageH - 12;

      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);

        // Set footer styling
        doc.setTextColor(0, 0, 0);
        doc.setFontSize(8);

        const leftMargin = 4;
        const rightMargin = 15;
        const formatLeftShift = 8; // Shift format to the right


        // LINE 2: Format on left (shifted right and bold), Page number on right
        doc.setFont("helvetica", "bold");
        const formatText = "Format No. F/OAC/21   Rev. 04 Dt. 09/07/2024";
        doc.text(formatText, leftMargin + formatLeftShift, footerStartY + 8, { align: "left" });

        doc.setFont("helvetica", "normal");
        const pageText = `Page ${i} of ${totalPages}`;
        doc.text(pageText, pageW - rightMargin, footerStartY + 8, { align: "right" });
      }
    };

    addFooterCallback();

    const fileName = `Automation_TC_${val("certificate_no", "tcds_no") || "certificate"}_${new Date().toISOString().split("T")[0]}.pdf`;
    const pdfBytes = doc.output('arraybuffer');
    return { fileName, pdfBytes };
  } catch (err) {
    console.error("Error generating Automation PDF:", err);
    throw err;
  }
}